import React from 'react';
import { PLOT_DRIVES_COLUMN_MAPPING } from './plotDrivesColumnMapping';
import { Table } from 'antd';
import {
  PLOT_MATRIX_GARDEN_CATEGORIES,
  PLOT_MATRIX_STEP_CATEGORIES,
} from './billSummaryCategories';
import {
  wallFieldToSpecId,
  wallSpecifications,
  WALLS_BUILD_UP_ITEM_CODE_MAP,
  resolveWallsBuildUpItemCode,
  getWallsBuildUpRateMultiplier,
} from '../mock/walls-section-build-up';
import {
  geoToberWallFieldToSpecId,
  GEO_WALL_BUILD_UP_ITEM_CODE_MAP,
  TOBERMORE_BUILD_UP_ITEM_CODE_MAP,
} from '../mock/geo-tober-wall-build-up';
import {
  resolveScreenWallsCode,
  resolveEOPillarCode,
  SCREEN_WALLS_M_J_SUPPLY_LABEL,
  EO_PILLAR_M_J_SUPPLY_LABEL,
} from './retainingWallsReferenceMapping';

// ─── Complete field → table column header label map ──────────────────────────
// Covers every field used across all PLOT_MATRIX_*_CATEGORIES.

export const FIELD_HEADER_LABELS: Record<string, string> = {
  // ── Site Strip ───────────────────────────────────────────────────────────
  vegStrip:                    'Site strip to remove vegetation',
  topsoilStrip:                'Excavate to strip topsoil, depth n.e. xxmm',
  topsoilDisposalOnsite:       'Topsoil Disposal onsite',
  topsoilDisposalOffsite:      'Topsoil Disposal offsite',
  // ── Cut / Fill ───────────────────────────────────────────────────────────
  cutFounds:  'Excavate to reduce level (foundations)',
  fillFounds: 'Fill to make up levels (foundations)',
  cutPaths:   'Excavate to reduce level (paths)',
  fillPaths:  'Fill to make up levels (paths)',
  cutGarden:  'Excavate to reduce level (gardens)',
  fillGarden: 'Fill to make up levels (gardens)',
  cutDrives:  'Excavate to reduce level (drives)',
  fillDrives: 'Fill to make up levels (drives)',
  // ── Pile Matts ───────────────────────────────────────────────────────────
  pileMat:       'Install xxxmm thick recycled 6F2 pile mat',
  pileMatRemoval:'Excavate to remove pile mat & re-use',
  // ── Foundation Exc & Conc ─────────────────────────────────────────────────
  plotGarSetOut:       'Engineer setting out houses & garages',
  pileSetOut:          'Setting out for piling contractor',
  pileAttendance:      'Attendance upon piling contractor',
  excavateTrench:      'Excavate trench and tip on site',
  excavateVibro:       'Excavate vibro foundation and tip on site',
  excavatePiled:       'Excavate trench piled foundation and tip on site',
  levelCompact:        'Consolidate bottoms of trenches',
  stockpileArisings:   'Deposit on site (arisings)',
  depositOnSite:       'Deposit on site',
  stepsInFounds:       'EO step at party wall',
  cropPiles:           'Pile crop',
  fillSteelPiles:      'Filling Steel Piles',
  concreteBlinding:    'Concrete blinding',
  stripClaymaster:     'Strip Claymaster',
  piledClaymaster:     'Piled Claymaster',
  depositCFAArisings:  'Deposit CFA arisings on site',
  pileCollar:          'Pile Collar',
  cordekCellcore155:   'Cordek Cellcore 155mm HX-B',
  cordekCellcore225:   'Cordek Cellcore 225mm HX-B',
  rebar:               'Steel Reinforcement (bar)',
  meshReinforcement:   'Mesh Reinforcement',
  formplasOrSimilar:   'Formwork to groundbeams (Formplas or similar)',
  gen1ConcreteToStrip: 'GEN 1 Concrete in foundation',
  rc35ConcreteToPiled: 'RC35 Concrete to Piled foundation',
  rc35ConcreteToVibro: 'RC35 Concrete to Vibro foundation',
  // ── Foundation Exc & Conc (Abnormals) ─────────────────────────────────────
  additionalExcavation:   'Additional Excavation',
  gen1AdditionalConcrete: 'Additional GEN 1 Concrete',
  // ── Foundations to DPC ───────────────────────────────────────────────
  trench4NBlock300mmWide:   'Trench block 300mm wide 3.6N',
  trench7NBlock300mmWide:   'Trench block 300mm wide 7.0N',
  trench4NBlock355mmWide:   'Trench block 355mm wide 3.6N',
  trench7NBlock355mmWide:   'Trench block 355mm wide 7.0N',
  dense4NBlockwork100mm:    '3.6N 100mm Dense Blockwork',
  dense7NBlockwork100mm:    'Dense concrete blockwork 100 thk 7n/mm2 (Ext Leaf)',
  dense10NBlockwork100mm:   '10N 100mm Dense Blockwork',
  aerated4NBlockwork100mm:  'Aerated concrete blockwork 100 thk 3.6n/mm2 (Ext leaf)',
  aerated7NBlockwork100mm:  'Aerated concrete blockwork 100 thk 7n/mm2 (Ext leaf)',
  aerated4NBlockwork140mm:  'Aerated concrete blockwork 140 thk 3.6n/mm2',
  aerated7NBlockwork140mm:  'Aerated concrete blockwork 140 thk 7n/mm2',
  dense100mmCoursingBrick:  'Dense HB Common Brickwork - 100mm',
  aerated100mmCoursingBrick:'Aerated HB Common Brickwork - 100mm',
  aerated140mmCoursingBrick:'Aerated HB Common Brickwork - 140mm',
  beamInfill:              'T Closure (dense 100 wide)',
  formCavity:              'Form Cavity',
  cavityFill:              'Cavity infill concrete',
  telescopicVents:         'Telescopic vents (incl. lintel) - 100 wide',
  telescopicVents140:      'Telescopic vents (incl. lintel) - 140 wide',
  perimeterInsulation:     '25mm Perimeter Strip',
  perimeterInsulation30:   '30mm Perimeter Strip',
  dpc100mm:                'DPC 100mm wide',
  backfillExcavations:     'Backfill with as dug',
  cavityInsulation:        'Cavity insulation',
  layFacingBrickwork:      'HB wall in facings FREE ISSUE',
  supplyFacingBrickwork:   'HB wall in facings M&J Supply',
  eoExposedBrickwork:      'EO exposed brickwork',
  eoTanking:               'EO tanking, inc retaining blockwork',
  // ── Foundations to DPC (Abnormals) ───────────────────────────────────
  additional300mmTrenchBlock: 'Additional 300mm Trench Block',
  additional355mmTrenchBlock: 'Additional 355mm Trench Block',
  additional100mmBlockwork:   'Additional 100mm Blockwork',
  backfillExcavationsAsDug:   'Backfill excavations as-dug',
  // ── Foundation Floor ──────────────────────────────────────────────────────
  bbFloorHouse:     'B&B floor House',
  bbFloorGarage:    'B&B floor Garage',
  insituFloorHouse: 'Insitu Floor House',
  insituFloorGarage:'Cast Insitu Slab - Garages',
  meshToFloor:      'Mesh to floor',
  topLayerMesh:     'Top layer Mesh',
  meshToGarageFloor:'Mesh to Garage floor',
  layOnlyFloors:    'Lay only Floors',
  topHats:          'Top Hats',
  eo2000gDPM:       'EO 2000g DPM',
  eoAmber1DPM:      'EO Amber 1 DPM',
  eoAmber2DPM:      'EO AMBER 2 DPM',
  radonMembrane:    'Radon Membrane',
  gasMembrane:      'Gas Membrane with validation',
  // ── Scaffold ─────────────────────────────────────────────────────────────
  scaffoldLevelCompact:   'Level & Compact (scaffold)',
  scaffoldGeotextileLayer:'Geotextile Layer (scaffold)',
  scaffoldCrush6F2:       'xxxmm crush 6F2 (scaffold)',
  scaffoldType1:          'xxmm Type1 (scaffold)',
  loadingLevelCompact:    'Level & Compact (loading bay)',
  loadingGeotextileLayer: 'Geotextile Layer (loading bay)',
  loadingCrush6F2:        'xxxmm crush 6F2 (loading bay)',
  loadingType1:           'xxmm Type1 (loading bay)',
  stairLevelCompact:      'Level & Compact (stair)',
  stairGeotextileLayer:   'Geotextile Layer (stair)',
  stairCrush6F2:          'xxxmm crush 6F2 (stair)',
  stairType1:             'xxmm Type1 (stair)',
  scaffoldPlatform:       'Scaffold Platform',
  // ── Drainage ─────────────────────────────────────────────────────────────
  osma32mmBend:       'Lab per plot (OSMA foul)',
  polypipe32mmBend:   'Lab per plot (Polypipe foul)',
  osmaStorm32mmBend:  'Lab per plot (OSMA storm)',
  polypipeStorm32mmBend:'Lab per plot (Polypipe storm)',
  // ── Plot Service / Service Connections ────────────────────────────────────
  plots:                  'Plots - plot services',
  apartments:             'Apartments',
  serviceConnectionPlots: 'Plots - service connections',
  serviceConnectionApartments: 'Apartments - service connections',
  serviceConnectionVacEx: 'Vac-ex - service connections',
  // ── Private Drives Binder ────────────────────────────────────────────────
  fillToMakeUp6F2:                      'Fill to make up levels with 6F2',
  macadamLevelCompact:                  'Level and compact formation (macadam)',
  macadamGeotextileLayer:               'Geotextile Layer (macadam)',
  macadamSubBase:                       'xxxmm thick MOT Type 1 (macadam)',
  blockLevelCompact:                    'Level and compact formation (block)',
  blockGeotextileLayer:                 'Geotextile Layer (block)',
  block300mmAdditionalCapping:          'xxxmm thick MOT Type 1 (block)',
  permeableLevelCompact:                'Level and compact formation (permeable)',
  permeableImpermeableGeotextileLayer:  'Terram Layer - Bottom & Sides (permeable)',
  permeable10to63mmCleanAggregateSubBase:'63-10mm Sub Base (permeable)',
  permeable5to20mmCleanAggregateSubBase: '5-20mm clean aggregate sub base (permeable)',
  permeableGeotextileLayer:             'Permeable geotextile membrane',
  sharedPermeableTimberEdgings:         'Timber Edgings',
  sharedPermablePccKerbs:               'PCC Kerbs',
  sharedPermeableKerbType1:             'xxxx Kerb (type 1)',
  sharedPermeableKerbType2:             'xxxx Kerb (type 2)',
  sharedPermeableKerbType3:             'xxxx Kerb (type 3)',
  sharedPermeableKeyKerb:               'Key Kerb',
  macadamDbBase:                        'xxmm DB Base (private macadam)',
  macadamDbBinder:                      'xxmm DB Binder (private macadam)',
  macadamSealingGrit:                   'Sealing Grit (private macadam)',
  block250mmSubBase:                    'xxmm DB Base (block paved)',
  block100mmDbBase:                     'xxmm DB Binder (block paved)',
  blockDbBinder:                        'DB Binder (block paved, legacy)',
  blockSealingGrit:                     'Sealing Grit (block paved)',
  permeableDbBinderRunningCourse:       'xxmm DB binder Running course (permeable)',
  // ── Shared Drives Binder ─────────────────────────────────────────────────
  sharedMacadamLevelCompact:    'Level and compact formation (shared macadam)',
  sharedMacadamGeotextileLayer: 'Geotextile Layer (shared macadam)',
  sharedMacadamSubBase:         'xxxmm thick MOT Type 1 (shared macadam)',
  sharedBlockLevelCompact:      'Level and compact formation (shared block)',
  sharedBlockGeotextileLayer:   'Geotextile Layer (shared block)',
  sharedBlockSubBase:           'xxxmm thick MOT Type 1 (shared block)',
  sharedPermeableLevelCompact:  'Level and compact formation (shared permeable)',
  sharedPermeableImpermeableGeotextileLayer: 'Terram Layer - Bottom & Sides (shared permeable)',
  sharedPermeable10to63mmCleanAggregateSubBase: '63-10mm Sub Base (shared permeable)',
  sharedPermeable5to20mmCleanAggregateSubBase:  '5-20mm clean aggregate sub base (shared permeable)',
  sharedPermeableGeotextileLayer: 'Permeable geotextile membrane (shared)',
  sharedMacadamDbBase:            'xxmm DB Base (shared macadam)',
  sharedMacadamDbBinder:          'xxmm DB Binder (shared macadam)',
  sharedMacadamSealingGrit:       'Sealing Grit (shared macadam)',
  sharedBlockBase:                'xxmm DB Base (shared block)',
  sharedBlockBinder:              'xxmm DB Binder (shared block)',
  sharedBlockSealingGrit:         'Sealing Grit (shared block)',
  sharedPermeableDbBinderRunningCourse: 'xxmm DB binder Running course (shared permeable)',
  // ── Screen / Retaining Walls ──────────────────────────────────────────────
  screenWalls:            'Screen Walls',
  screenWallsEO:          'Screen Walls EO Pillar (Nr)',
  screenWallsSof:         'Screen Walls Sof 450mm (m)',
  screenWallsLog:         'Screen Walls Log 1200mm (m)',
  screenWallsGravelBoard: 'Screen Walls Gravel Board (m)',
  screenWallsAdditionalExcavation: 'Screen Walls Additional Excavation (m3)',
  screenWallsAdditionalConcrete: 'Screen Walls Additional Concrete (m3)',
  grw450:   'Garden Retaining Wall 450 (m)',
  grw600:   'Garden Retaining Wall 600 (m)',
  grw750:   'Garden Retaining Wall 750 (m)',
  grw900:   'Garden Retaining Wall 900 (m)',
  grw1050:  'Garden Retaining Wall 1050 (m)',
  grw1200:  'Garden Retaining Wall 1200 (m)',
  grw1500:  'Garden Retaining Wall 1500 (m)',
  grw1800:  'Garden Retaining Wall 1800 (m)',
  grw2000:  'Garden Retaining Wall 2000 (m)',
  drw450:   'Drive Retaining Wall 450 (m)',
  drw600:   'Drive Retaining Wall 600 (m)',
  drw750:   'Drive Retaining Wall 750 (m)',
  drw900:   'Drive Retaining Wall 900 (m)',
  drw1050:  'Drive Retaining Wall 1050 (m)',
  drw1200:  'Drive Retaining Wall 1200 (m)',
  drw1500:  'Drive Retaining Wall 1500 (m)',
  drw1800:  'Drive Retaining Wall 1800 (m)',
  drw2000:  'Drive Retaining Wall 2000 (m)',
  drw2250:  'Drive Retaining Wall 2250 (m)',
  drw2500:  'Drive Retaining Wall 2500 (m)',
  drw2750:  'Drive Retaining Wall 2750 (m)',
  drw3000:  'Drive Retaining Wall 3000 (m)',
  ccrw1500: 'Concrete Core Wall 1500 (m)',
  ccrw1800: 'Concrete Core Wall 1800 (m)',
  ccrw2000: 'Concrete Core Wall 2000 (m)',
  ccrw2250: 'Concrete Core Wall 2250 (m)',
  ccrw2500: 'Concrete Core Wall 2500 (m)',
  ccrw2750: 'Concrete Core Wall 2750 (m)',
  ccrw3000: 'Concrete Core Wall 3000 (m)',
  ccrw3250: 'Concrete Core Wall 3250 (m)',
  ccrw3500: 'Concrete Core Wall 3500 (m)',
  geoWallGPCW600: 'GPCW 600mm (m)',
  gpcw750:        'GPCW 750mm (m)',
  geoWallRW900:   'GPCW 900mm (m)',
  gpcw1050:       'GPCW 1050mm (m)',
  tpcw1: 'TPCW 1 (m)',
  tpcw2: 'TPCW 2 (m)',
  tpcw3: 'TPCW 3 (m)',
  tpcw4: 'TPCW 4 (m)',
  tpcw5: 'TPCW 5 (m)',
  tpcw6: 'TPCW 6 (m)',
  tpcw7: 'TPCW 7 (m)',
  tpcw8: 'TPCW 8 (m)',
  // ── Plot Pavings ──────────────────────────────────────────────────────────
  pavingLevelCompact:    'Level and compact formation (paving)',
  pavingGeotextileLayer: 'Geotextile membrane (paving)',
  pavingSubBase:         'xxxmm thick Stone bed (paving)',
  pavingPlainGreyPavings:'Slabs',
  slab900wSteps:         'EO for forming steps - 900mm wide',
  slab1200wSteps:        'EO for forming steps - 1200mm wide',
  slabCycleHoops:        'Sheffield cycle hoops',
  slabRotaryDryer:       'Fix only rotary dryers',
  slabThresholdDrain:    'Threshold drain to fronts only',
  slabShedBase:          'Shed Base',
  slabGravelMarginPea:   '10mm/20mm gravel in 150mm wide margin to house path',
  slabGravelMarginCotswold: 'Cotswold gravel in 150mm wide margin to house path',
  macadamDbSurfaceCourse:'xxmm DB Surface course',
  macadamHraSurfaceCourse:'xxmm HRA Surface course',
  macadamSmaSurfaceCourse:'xxmm SMA Surface course',
  macadamSweepTack:      'Sweep & Tack coat',
  macadamEdgings:        'Edgings 150 x 50',
  // ── Garden / Topsoil ─────────────────────────────────────────────────────
  spreadLevelCleanSubsoilCapImported: 'Spread & level imported clean subsoil capping layer',
  spreadLevelCleanSubsoilCapSiteWon:  'Spread & level site won clean subsoil capping layer',
  thickNoDigLayer:            'xxxmm thick no-dig layer',
  geotextileLayer:            'Geotextile Layer',
  spreadLevelTopsoilImported: 'Spread & level imported topsoil',
  spreadLevelTopsoilSiteWon:  'Spread & level site won topsoil',
  harrowGardens:              'Harrow gardens to 300mm depth',
  plotClearance:              'Remove builders rubbish prior to topsoiling',
  // ── Private/Shared Drives Surface ────────────────────────────────────────
  permeableTimberEdgings:         'Timber Edgings',
  macadamRemoveTimberEdgings:     'Remove Timber Edgings (private macadam)',
  sharedPermeableEdgingRemedials: 'Edging Remedials',
  sharedPermeableKerbRemedials:   'Kerb Remedials',
  sharedPermeableAdjustIronwork:  'Adjust Ironwork',
  sharedPermeableDriveDemo:       'Drive Demarcation',
  blockPunctureMacadam:           'Puncture Macadam (private block)',
  permeablePunctureMacadam:       'Puncture Macadam (private permeable)',
  sharedBlockPunctureMacadam:     'Puncture Macadam (shared block)',
  sharedPermeablePunctureMacadam: 'Puncture Macadam (shared permeable)',
  sharedPermeableRecessedCF:      'Recessed C&F',
  macadamTack:                    'Sweep & Tack',
  blockSweep:                     'Sweep',
  blockLayingCourse:              'xxmm sand Laying Course',
  block60mmConcrete:              '60mm Concrete paviors',
  block80mmConcrete:              '80mm Concrete paviors',
  permeable2to6mmLayingAggregate: 'xxmm thick 2-6mm laying Aggregate',
  permeable60mmConcrete:          '60mm Concrete paviors (permeable)',
  permeable80mmConcrete:          '80mm Concrete paviors (permeable)',
  sharedMacadamDbSurfaceCourse:   'xxmm DB Surface course (shared macadam)',
  sharedMacadamHraSurfaceCourse:  'xxmm HRA Surface course (shared macadam)',
  sharedMacadamSmaSurfaceCourse:  'xxmm SMA Surface course (shared macadam)',
  sharedMacadamSweepTack:         'Sweep & Tack (shared macadam)',
  sharedBlock60mmConcrete:        '60mm Concrete paviors (shared block)',
  sharedBlock80mmConcrete:        '80mm Concrete paviors (shared block)',
  sharedBlockLayingCourse:        'xxmm sand Laying Course (shared block)',
  sharedPermeable60mmConcrete:    '60mm Concrete paviors (shared permeable)',
  sharedPermeable80mmConcrete:    '80mm Concrete paviors (shared permeable)',
  sharedPermeable2to6mmLayingAggregate: 'xxmm thick 2-6mm laying Aggregate (shared permeable)',
  sharedPermeable150x50mmPccEdgings:    '150x50mm PCC Edgings',
  sharedPermeable200x50mmPccEdgings:    '200x50mm PCC Edgings',
};

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FormulaRow {
  key: string;
  columnHeader: string;
  fieldName: string;
  resourceCode: string;
  rateSource: string;
  note: string;
  rate?: number;
  modifiedRate?: number;
}

interface FormulaPart {
  label?: string;
  rows: FormulaRow[];
}

interface FormulaDefinition {
  summary: string;
  parts: FormulaPart[];
}

// ─── Field → resource/evaluate code map ──────────────────────────────────────
// '[dynamic]' = code resolved at runtime from user dimension selections.
// ''          = no direct item library code (calculated or build-up derived).

export const FIELD_RESOURCE_CODES: Record<string, string> = {
  // ── Site Strip / Cut & Fill ───────────────────────────────────────────────
  vegStrip:               'ALP033b',
  topsoilStrip:           'GWALP001',
  topsoilDisposalOnsite:  'GWALP035',
  topsoilDisposalOffsite: 'AMAS005',
  cutFounds:  'GWALP002', cutPaths:   'GWALP002',
  cutGarden:  'GWALP002', cutDrives:  'GWALP002',
  fillFounds: 'GWALP003', fillPaths:  'GWALP003',
  fillGarden: 'GWALP003', fillDrives: 'GWALP003',
  fillToMakeUp6F2: 'GWALP004',
  // ── Pile Matts ───────────────────────────────────────────────────────────
  pileMat:        'GWALP004',
  pileMatRemoval: 'GWALP002',
  // ── Foundation Exc & Conc ─────────────────────────────────────────────────
  plotGarSetOut:       'GWALP039',
  pileSetOut:          'GWALP016',
  pileAttendance:      'GWALP015',
  excavateTrench:      'GWALP010',
  excavatePiled:       'GWALP018',
  excavateVibro:       'GWALP018',
  levelCompact:        'GWALP012',
  stockpileArisings:   'GWALP035',
  depositOnSite:       'GWALP035',
  stepsInFounds:       'GWALP011',
  cropPiles:           'GWALP017',
  fillSteelPiles:      'GWALP052',
  concreteBlinding:    'GWALP013a',
  formplasOrSimilar:   'GWALP020',
  rebar:               'GWALP024',
  meshReinforcement:   '[dynamic]',
  gen1ConcreteToStrip: '[dynamic]',
  rc35ConcreteToPiled: '[dynamic]',
  rc35ConcreteToVibro: '[dynamic]',
  stripClaymaster:     'GWALP022',
  piledClaymaster:     'GWALP022',
  depositCFAArisings:  'ALP033a',
  pileCollar:          'GWALP017a',
  cordekCellcore155:   'GWALP021B',
  cordekCellcore225:   'GWALP021',
  // Abnormals
  additionalExcavation:   'GWALP010',
  gen1AdditionalConcrete: '[dynamic]',
  // ── Foundations to DPC ───────────────────────────────────────────────
  trench4NBlock300mmWide:   'GWALPS003',
  trench7NBlock300mmWide:   'GWALPS005',
  trench4NBlock355mmWide:   'GWALPS004',
  trench7NBlock355mmWide:   'GWALPS006',
  dense4NBlockwork100mm:    'GWALPS047',
  dense7NBlockwork100mm:    'GWALPS019',
  dense10NBlockwork100mm:   'GWALPS048',
  aerated4NBlockwork100mm:  'GWALPS045',
  aerated7NBlockwork100mm:  'GWALPS007',
  aerated4NBlockwork140mm:  'GWALPS046',
  aerated7NBlockwork140mm:  'GWALPS008',
  dense100mmCoursingBrick:  'GWALPS020',
  aerated100mmCoursingBrick:'GWALPS032',
  aerated140mmCoursingBrick:'GWALPS033',
  beamInfill:              'GWALPS024',
  formCavity:              'GWALPS013',
  cavityFill:              'GWALPS014',
  telescopicVents:         'GWALPS015',
  telescopicVents140:      'GWALPS023',
  perimeterInsulation:     'GWALPS026',
  perimeterInsulation30:   'GWALPS027',
  dpc100mm:                'GWALPS016',
  backfillExcavations:     'GWALP034',
  cavityInsulation:        'GWALPS025',
  layFacingBrickwork:      'GWALPS011',
  supplyFacingBrickwork:   'GWALPS018',
  eoExposedBrickwork:      'GWALPS017',
  eoTanking:               'GWALPS017A',
  // Abnormals
  additional300mmTrenchBlock: 'GWALPS005',
  additional355mmTrenchBlock: 'GWALPS006',
  additional100mmBlockwork:   'GWALPS007',
  backfillExcavationsAsDug:   'GWALP034',
  // ── Foundation Floor ──────────────────────────────────────────────────────
  bbFloorHouse:      '[dynamic]',
  bbFloorGarage:     '[dynamic]',
  insituFloorHouse:  'GWALPF012',
  insituFloorGarage: 'GWALPF012',
  layOnlyFloors:     'GWALPF031',
  meshToFloor:       '[dynamic]',
  topLayerMesh:      '[dynamic]',
  meshToGarageFloor: '[dynamic]',
  topHats:           'GWALPF013',
  eo2000gDPM:        'CA0004',
  eoAmber1DPM:       'ZEV24',
  eoAmber2DPM:       'ZEV25',
  radonMembrane:     'SCBB006',
  gasMembrane:       'SCBB006',
  // ── Scaffold ─────────────────────────────────────────────────────────────
  scaffoldLevelCompact:    'GWALP005',
  scaffoldGeotextileLayer: 'ALP031A',
  scaffoldCrush6F2:        'GWALP004',
  scaffoldType1:           'GWALP006',
  loadingLevelCompact:     'GWALP005',
  loadingGeotextileLayer:  'ALP031A',
  loadingCrush6F2:         'GWALP004',
  loadingType1:            'GWALP006',
  stairLevelCompact:       'GWALP005',
  stairGeotextileLayer:    'ALP031A',
  stairCrush6F2:           'GWALP004',
  stairType1:              'GWALP006',
  // ── Drainage ─────────────────────────────────────────────────────────────
  osma32mmBend:              'GWALPDD001A',
  polypipe32mmBend:          'GWALPDD001A',
  osmaStorm32mmBend:         'GWALPDD001A',
  polypipeStorm32mmBend:     'GWALPDD001A',
  osma600mmNmePpic:          'GWALPDD052',
  osmaStorm600mmNmePpic:     'GWALPDD052',
  polypipe600mmNmePpic:      'GWALPDD052',
  polypipeStorm600mmNmePpic: 'GWALPDD052',
  // ── Plot Service / Service Connections ────────────────────────────────────
  plots:                       'GWALPDS006',
  apartments:                  'GWALPDS006',
  serviceConnectionPlots:      'GWALPDS007',
  serviceConnectionApartments: 'GWALPDS007',
  serviceConnectionVacEx:      'GWALPDS003',
  // ── Private Drives Binder ────────────────────────────────────────────────
  macadamLevelCompact:                  'GWALP005',
  macadamGeotextileLayer:               'ALP031A',
  macadamSubBase:                       'GWALP006',
  macadamDbBase:                        '[dynamic]',
  macadamDbBinder:                      '[dynamic]',
  macadamSealingGrit:                   'ALP054',
  blockLevelCompact:                    'GWALP005',
  blockGeotextileLayer:                 'ALP031A',
  block300mmAdditionalCapping:          'GWALP006',
  block250mmSubBase:                    'GWALP006',
  block100mmDbBase:                     '[dynamic]',
  blockDbBinder:                        '',
  blockSealingGrit:                     'ALP054',
  permeableLevelCompact:                'GWALP005',
  permeableImpermeableGeotextileLayer:  'GWALP014',
  permeable10to63mmCleanAggregateSubBase:'GWALP028',
  permeable5to20mmCleanAggregateSubBase: 'GWALP028',
  permeableGeotextileLayer:             'GWALP014a',
  permeableDbBinderRunningCourse:       '[dynamic]',
  sharedPermeableTimberEdgings:         'GWALP032',
  sharedPermablePccKerbs:               'ALP083',
  sharedPermeableKerbType1:             '[dynamic]',
  sharedPermeableKerbType2:             '[dynamic]',
  sharedPermeableKerbType3:             '[dynamic]',
  sharedPermeableKeyKerb:               'ALP103',
  // ── Shared Drives Binder ─────────────────────────────────────────────────
  sharedMacadamLevelCompact:    'GWALP005',
  sharedMacadamGeotextileLayer: 'ALP031A',
  sharedMacadamSubBase:         'GWALP006',
  sharedMacadamDbBase:          '[dynamic]',
  sharedMacadamDbBinder:        '[dynamic]',
  sharedMacadamSealingGrit:     'ALP054',
  sharedBlockLevelCompact:      'GWALP005',
  sharedBlockGeotextileLayer:   'ALP031A',
  sharedBlockSubBase:           'GWALP006',
  sharedBlockBase:              '[dynamic]',
  sharedBlockBinder:            '[dynamic]',
  sharedBlockSealingGrit:       'ALP054',
  sharedPermeableLevelCompact:  'GWALP005',
  sharedPermeableImpermeableGeotextileLayer: 'GWALP014',
  sharedPermeable10to63mmCleanAggregateSubBase: 'GWALP028',
  sharedPermeable5to20mmCleanAggregateSubBase:  'GWALP028',
  sharedPermeableGeotextileLayer: 'GWALP014a',
  sharedPermeableDbBinderRunningCourse: '[dynamic]',
  sharedPermeableRecessedCF:      'GWALPD013',
  sharedPermeableAdjustIronwork:  'GWALPD011',
  sharedPermeableDriveDemo:       'GWALPD012',
  sharedPermeableEdgingRemedials: 'ALP085',
  sharedPermeableKerbRemedials:   'ALP086',
  sharedPermeable150x50mmPccEdgings: 'ALP027',
  sharedPermeable200x50mmPccEdgings: 'ALP027A',
  // ── Screen / Retaining Walls ──────────────────────────────────────────────
  screenWalls:            'GWALPGW003',
  screenWallsEO:          'GWALPGW003A',
  screenWallsSof:         'GWALPGW0043',
  screenWallsLog:         'ACL012A',
  screenWallsGravelBoard: 'GWALPGW0023',
  screenWallsAdditionalExcavation: 'GWALPGW0026',
  screenWallsAdditionalConcrete: 'GWALPGW0015',
  // GRW/DRW/CCRW/GPCW/TPCW use build-up netPerM — no single evaluate code
  // ── Plot Pavings ──────────────────────────────────────────────────────────
  pavingLevelCompact:    'GWALP005',
  pavingGeotextileLayer: 'ALP031A',
  pavingSubBase:         'GWALP006',
  pavingPlainGreyPavings:'[dynamic]',
  slab900wSteps:         'GWALP046',
  slab1200wSteps:        'GWALP046A',
  slabGravelMarginPea:   'GWALP009',
  slabGravelMarginCotswold:'GWALP009A',
  slabThresholdDrain:    'GWALPDD050',
  slabShedBase:          'GWALP047',
  slabCycleHoops:        'GWALP048',
  slabRotaryDryer:       'GWALP049',
  macadamTack:           'ALP055',
  macadamSweepTack:      'ALP055',
  macadamDbSurfaceCourse:'[dynamic]',
  macadamHraSurfaceCourse:'[dynamic]',
  macadamSmaSurfaceCourse:'[dynamic]',
  macadamEdgings:        'ALP027',
  blockSweep:            'GWALPD020',
  blockLayingCourse:     'GWALPD019',
  block60mmConcrete:     '[dynamic]',
  block80mmConcrete:     '[dynamic]',
  blockPunctureMacadam:  'GWALPD022',
  permeablePunctureMacadam: 'GWALPD022',
  sharedBlockPunctureMacadam: 'GWALPD022',
  sharedPermeablePunctureMacadam: 'GWALPD022',
  permeable2to6mmLayingAggregate: 'GWALP029',
  permeable60mmConcrete:  '[dynamic]',
  permeable80mmConcrete:  '[dynamic]',
  sharedMacadamSweepTack: 'ALP055',
  sharedMacadamDbSurfaceCourse:  '[dynamic]',
  sharedMacadamHraSurfaceCourse: '[dynamic]',
  sharedMacadamSmaSurfaceCourse: '[dynamic]',
  sharedBlock60mmConcrete: '[dynamic]',
  sharedBlock80mmConcrete: '[dynamic]',
  sharedBlockLayingCourse: 'GWALPD019',
  sharedPermeable60mmConcrete: '[dynamic]',
  sharedPermeable80mmConcrete: '[dynamic]',
  sharedPermeable2to6mmLayingAggregate: 'GWALP029',
  permeableTimberEdgings: 'GWALP032',
  macadamRemoveTimberEdgings: 'GWALP032a',
  // ── Garden / Topsoil ─────────────────────────────────────────────────────
  thickNoDigLayer:                    'GWALPGW033',
  geotextileLayer:                    'GWALP014',
  plotClearance:                      'GWALPGW001',
  spreadLevelCleanSubsoilCapSiteWon:  'GWALPGW034',
  spreadLevelCleanSubsoilCapImported: 'GWALPGW036',
  harrowGardens:                      'GWALPGW042',
  spreadLevelTopsoilSiteWon:          'GWALPGW002',
  spreadLevelTopsoilImported:         'GWALPGW035',
};

// ─── Row builder ─────────────────────────────────────────────────────────────

function row(
  field: string,
  rateSource: string,
  resourceCode?: string,
  note = '',
): FormulaRow {
  const code = resourceCode !== undefined ? resourceCode : (FIELD_RESOURCE_CODES[field] ?? '');
  return {
    key: field,
    columnHeader: FIELD_HEADER_LABELS[field] ?? field,
    fieldName: field,
    resourceCode: code,
    rateSource,
    note,
  };
}

/**
 * Resolve a Plot Matrix column's `fieldNames` + a take-off row into the
 * list of `{ code, qty }` resource entries the central-repo expects on
 * each task. Used by the central-repo submission (`GroundworksReview.tsx`)
 * to attach `resource: { code: string; qty: number }[]` to every task in
 * the payload — pairs each BoQ resource code with the actual activity
 * quantity from this plot's row of the corresponding step's take-off
 * table.
 *
 * - Drops fields with no entry in `FIELD_RESOURCE_CODES`.
 * - Drops empty-string codes (fields whose code is "" — no direct
 *   item-library code, e.g. build-up-derived nets, computed amounts).
 * - Drops the `[dynamic]` sentinel — these fields have a code that
 *   depends on runtime dimension selections; downstream consumers
 *   should resolve them via the active soil-management / concrete grade
 *   picks if they need the literal code.
 * - Aggregates duplicate codes by summing qtys, so a category whose
 *   fields share a code (e.g. cutFounds / cutPaths / cutGarden all map
 *   to GWALP002) emits a single entry with the combined qty.
 * - Drops entries whose aggregated qty is 0 — central repo doesn't need
 *   to log zero-quantity activities.
 * - Returns `[]` when no field has a usable code or no row is supplied.
 *
 * The `row` argument is the plot's row from the column's step's table
 * (foundationsTable, transformed plotDrivesTable, etc.). Field qtys are
 * read directly from this row using the field name as the key.
 */
export function resolveFieldResources(
  fieldNames: readonly string[],
  row: Record<string, any> | undefined,
): { code: string; qty: number }[] {
  if (!row) return [];
  const aggregated = new Map<string, number>();
  fieldNames.forEach((field) => {
    const code = FIELD_RESOURCE_CODES[field];
    if (!code || code === '[dynamic]') return;
    const qty = Number(row[field]);
    if (!Number.isFinite(qty)) return;
    aggregated.set(code, (aggregated.get(code) ?? 0) + qty);
  });
  const out: { code: string; qty: number }[] = [];
  aggregated.forEach((qty, code) => {
    if (qty !== 0) out.push({ code, qty });
  });
  return out;
}

// Pre-computed `field → number of Plot Matrix Garden columns that include it`.
// Mirrors the `fieldOccurrence` map built inside `computeGardenFieldRates`,
// used by `resolveTaskResources` to divide a duplicated Garden field's qty
// across the columns it appears in (so summing the per-task contributions
// across all Garden tasks yields one `rowQty × rate` per field, not N copies).
const GARDEN_FIELD_OCCURRENCE: Map<string, number> = (() => {
  const m = new Map<string, number>();
  Object.values(PLOT_MATRIX_GARDEN_CATEGORIES).forEach((fields) => {
    (fields as readonly string[]).forEach((f) => {
      m.set(f, (m.get(f) ?? 0) + 1);
    });
  });
  return m;
})();

/**
 * Soil-management state read from the Configure Headers picks. Mirrors the
 * shape `computeSoilBoQTotals` and the formula-inspector helpers expect.
 */
type SoilManagementContext = {
  topsoilManagement?: { code?: string };
  subsoilManagement?: { code?: string };
};

/**
 * Persisted retaining-walls build-up spec (matches `RetainingWallsBuildUpSpecification`
 * from `src/api/retainingWallsBuildUp.ts`). Kept structurally typed here so
 * this module doesn't take a direct dependency on the API type.
 */
type RetainingWallsBuildUpSpecLike = {
  id: string;
  items?: Array<{ id?: string; description?: string; net?: number }>;
};

/**
 * Persisted geo / Tobermore wall build-up spec (matches
 * `GeoToberWallBuildUpSpecification` from `src/api/wallSectionBuildUp.ts`).
 * `type` distinguishes which BuildUpItem→code map applies — `'geowall'` →
 * `GEO_WALL_BUILD_UP_ITEM_CODE_MAP`, anything else (e.g. `'tobermore'`) →
 * `TOBERMORE_BUILD_UP_ITEM_CODE_MAP`.
 */
type GeoToberWallBuildUpSpecLike = {
  id: string;
  type?: string;
  items?: Array<{ id?: string; description?: string; net?: number }>;
};

type ResolveTaskResourcesContext = {
  groundworks: SoilManagementContext & Record<string, any>;
  /**
   * Map of upper-cased resource code → item-library nettRate. Built by
   * `buildEvaluateRateMap(itemLibraryData)` in `plotMatrixCategoryGenerator`.
   * Used only for the soil-management columns (where the resource code is
   * resolved at runtime from Configure Headers and qty is derived from the
   * cell value as `cellTotal ÷ rate`).
   */
  itemLibraryRateByCode?: Map<string, number>;
  numberOfPlots: number;
  /**
   * Map of `fieldName → numeric multiplier` lifted from the BoQ summary
   * payload (`BoqSummaryLineItem.multiplier`). Populated by walking the
   * groundworks BoQ summary tree and keeping every numeric multiplier (the
   * apartments multiple on Plot Service apartments lines, retaining-walls
   * `net × N` build-up children, etc.). String multipliers (m³→m² thickness
   * dimensions like "150mm") are parsed into metres before insertion — see
   * `parseDimensionMultiplier` in `GroundworksReview.tsx`.
   *
   * `resolveTaskResources` applies it as `qty = rawQty × multiplier` per
   * field so the central-repo task's resource cost (`Σ qty × rate(code)`)
   * matches the BoQ summary's amount (where the multiplier is also baked
   * into the line item's rate). The multiplier is also surfaced on the
   * returned resource entry's `multiplier` field so the central repo can
   * see it as metadata alongside the multiplied qty.
   */
  multipliersByField?: Map<string, number>;
  /**
   * Fields whose `multipliersByField` value came from an M³-conversion
   * thickness string (e.g. `pileMat`, `concreteBlinding`). Only resources
   * whose source field is in this set will have a `multiplier` key on the
   * returned entry — apartments / retaining-walls numeric multipliers still
   * adjust qty but the metadata key stays off so consumers can tell at a
   * glance which entries are thickness conversions.
   */
  thicknessFields?: Set<string>;
  /**
   * `fieldName → resolved resourceCode` lifted from the BoQ summary's
   * per-line `resourceCode`. Used in preference to the static
   * `FIELD_RESOURCE_CODES` map so dynamically-resolved Foundations codes
   * (mesh type via `MESH_TYPE_TO_CODE`, concrete grade per
   * `foundationsPrefixValues`, etc.) reach the central-repo
   * payload — `FIELD_RESOURCE_CODES` only has `'[dynamic]'` / empty strings
   * for these and would silently drop the entries.
   */
  codesByField?: Map<string, string>;
  /**
   * Persisted retaining-walls build-up specs (from `useGetRetainingWallsBuildUp`).
   * Used by the retaining-walls expansion branch in `resolveTaskResources`
   * to emit one resource per build-up item (blockwork courses, facing brick
   * widths, BoE coping widths, etc.) for any GRW/DRW/CCRW wall-spec field
   * the column carries. Without this, the central-repo Retaining Walls
   * task only emits the screen-walls direct-code fields and silently drops
   * every build-up sub-item — the user-facing symptom is "retaining walls
   * stage doesn't include all resources for build-up".
   *
   * Resolution path per build-up item:
   *   • `code` = `WALLS_BUILD_UP_ITEM_CODE_MAP[item.id]` (facing-brickwork
   *     uses `resolveWallsBuildUpItemCode` so the GWALPGW0020 / GWALPGW0021
   *     dropdown choice is honoured).
   *   • `multiplier` = `multipliersByField.get(item.id)` (= `item.net × N`
   *     from the BoQ summary, where N is the rate multiplier — 1 for
   *     non-blockwork / non-facing-brick / non-BoE-coping rows).
   *   • `qty` = `wallQtyM × multiplier`, `actualQty` = `wallQtyM`.
   */
  retainingWallsBuildUpSpecs?: RetainingWallsBuildUpSpecLike[];
  /**
   * Persisted geo / Tobermore wall build-up specs (from
   * `useGetGeoToberWallBuildUp`). Used by the geo-walls expansion branch
   * in `resolveTaskResources` — same pattern as `retainingWallsBuildUpSpecs`
   * but for the GPCW / GeoWall RW / TPCW columns. Each build-up item emits
   * one resource with `qty = wallQtyM × multiplier`, `multiplier = item.net`
   * (geo-walls have no rate multiplier `N`, so the multiplier is just `net`
   * — matches `multiplier: net` in `computeGeoToberWallsSection`).
   */
  geoToberWallBuildUpSpecs?: GeoToberWallBuildUpSpecLike[];
  /**
   * Plot number for the row currently being resolved. Required because the
   * 'Ret & Screen Walls' step's primary row (`retainingWallsTable`) might be
   * missing for plots that only have geo / Tobermore wall data — without an
   * explicit `plotNo` the cross-table lookup to `geoToberWallTable` can't
   * derive its key from `row?.plotNo`. Passed from the central-repo POST at
   * the `formatPlotMatrixForSubmission` plot iteration level.
   */
  plotNo?: string;
  /**
   * `BoQ summary category → [{ code, quantity }]` lookup used by the
   * Rule-C short-circuit in `resolveTaskResources` (Drainage / Plot Service /
   * Service Connections). The Plot Matrix columns for these steps only
   * carry one or two fields in `fieldNames` (e.g. just `plots` for Plot
   * Services), so iterating `fieldNames` produces an incomplete resource
   * list — the BoQ summary's drainage / plotService sections enumerate the
   * full set (every PPIC, gully, drainage channel, every plot-service field
   * etc.). When provided, the Rule-C branch falls back to this lookup
   * instead of the standard per-field path, emitting one resource per
   * summary item with `qty = aggregate / numberOfPlots` (the per-plot
   * share). Built by walking `summary.plotService` + `summary.drainage` in
   * `buildBoqSummaryLookups`.
   */
  summaryItemsByCategory?: Map<
    string,
    Array<{ code: string; quantity: number; rate: number; multiplier?: number }>
  >;
};

/**
 * Resolve a Plot Matrix task's resource list using the **same formula the
 * Plot Matrix cell uses** — so summing `qty × rate(code)` across the
 * returned entries approximates the cell value (which is also what
 * `stage.total` in the central-repo payload carries).
 *
 *   Column type                                     │ resource qty
 *   ────────────────────────────────────────────────┼────────────────────────────
 *   Cut & Fills (non-soil) / Foundations / Plot     │ rowQty            (literal
 *   Drives / Plot Pavings / Scaffold / Walls        │  qty × rate)
 *   Garden                                          │ rowQty ÷ occ      (mirrors
 *                                                   │  the `/occ` divisor on
 *                                                   │  duplicated fields)
 *   Drainage / Plot Service / Service Connections   │ rowQty ÷ numberOfPlots
 *                                                   │  (cell = aggregateTotal ÷
 *                                                   │  numberOfPlots; each plot
 *                                                   │  carries 1/N of the
 *                                                   │  aggregate quantity)
 *   Cut & Fills soil-management columns             │ single derived entry:
 *   (Topsoil Disposal, Disposal Of Surplus,         │  cellTotal ÷ rate(code)
 *   Import of Deficit)                              │  where code = active
 *                                                   │  soil-management code
 *                                                   │  from Configure Headers
 *
 * Aggregation, code filtering (`[dynamic]` sentinel + empty / unmapped codes)
 * and zero-qty dropping mirror `resolveFieldResources`. Returns `[]` when
 * neither the row nor the soil-management code yields a usable entry.
 */
export function resolveTaskResources(
  column: { stepName: string; categoryName: string; fieldNames: readonly string[] },
  row: Record<string, any> | undefined,
  cellTotal: number,
  context: ResolveTaskResourcesContext,
): { code: string; qty: number; actualQty: number; multiplier?: number }[] {
  const { stepName, categoryName, fieldNames } = column;
  const {
    groundworks,
    itemLibraryRateByCode,
    numberOfPlots,
    multipliersByField,
    thicknessFields,
    codesByField,
    retainingWallsBuildUpSpecs,
    geoToberWallBuildUpSpecs,
    summaryItemsByCategory,
    plotNo: contextPlotNo,
  } = context;

  // ── Garage short-circuit for 'Ret & Screen Walls': retaining walls and
  // geo/Tobermore walls are a plot-only concern — garages (`G-*` plot
  // numbers) shouldn't ship wall build-up resources to the central repo.
  // Otherwise `findStepPlotRow` for 'Ret & Screen Walls' (which doesn't
  // filter by `plotType`, unlike Foundations/Scaffold) collides on the
  // normalized plotKey ("1" matches both "P-1" and "G-1") and returns the
  // plot's retainingWallsTable row to the garage's `resolveTaskResources`
  // call, causing G-1/G-2/… to emit the same wall build-up resources as
  // their paired plot. Returning `[]` here keeps the garage's Retaining
  // Walls stage `tasks[].resource` empty, which is what the user wants.
  const isGaragePlot = String(contextPlotNo ?? '').trim().toUpperCase().startsWith('G-');
  if (stepName === 'Ret & Screen Walls' && isGaragePlot) {
    return [];
  }

  // ── Garage short-circuit for Rule-C steps (Drainage / Plot Service /
  // Service Connections): garages have no drainage or plot-service costs.
  // The Rule-C short-circuit below doesn't inspect `row` — it distributes
  // the aggregate equally to every caller, so without this guard a garage
  // row would receive the same per-plot share as a real plot, doubling the
  // aggregate when totals are summed across all submissions.
  const isRuleCStep =
    stepName === 'Drainage' ||
    stepName === 'Plot Service' ||
    stepName === 'Service Connections';
  if (isRuleCStep && isGaragePlot) {
    return [];
  }

  // ── Soil management columns: cell = aggregateAmount ÷ numberOfPlots, and
  // the active resource code lives on Configure Headers (resolved by
  // computeSoilBoQTotals). Emit a single derived entry so qty × rate = cell.
  const isTopsoilDisposal =
    stepName === 'Cut & Fills' && categoryName === 'Site Strip - Topsoil Disposal';
  const isSubsoilSurplus =
    stepName === 'Cut & Fills' && categoryName === 'Cut/Fill - Disposal Of Surplus';
  const isSubsoilDeficit =
    stepName === 'Cut & Fills' && categoryName === 'Cut/Fill - Import of Deficit';

  if (isTopsoilDisposal || isSubsoilSurplus || isSubsoilDeficit) {
    if (!cellTotal || cellTotal === 0) return [];
    const mgmt = isTopsoilDisposal
      ? groundworks?.topsoilManagement
      : groundworks?.subsoilManagement;
    const rawCode = mgmt?.code?.trim();
    if (!rawCode || rawCode.toUpperCase() === 'NONE') return [];
    const code = rawCode.toUpperCase();
    const rate = itemLibraryRateByCode?.get(code) ?? 0;
    if (rate === 0) return [];
    // Soil-management columns have no per-field multiplier (cellTotal is
    // already the multiplied amount). `actualQty` mirrors `qty` so the
    // shape of every resource entry stays uniform across all column types.
    const soilQty = cellTotal / rate;
    return [{ code: rawCode, qty: soilQty, actualQty: soilQty }];
  }

  // ── Rule-C steps: cell = aggregateTotal ÷ numberOfPlots, so this plot's
  // share of each field's quantity is `rowQty ÷ numberOfPlots`. Multiplying
  // by the item-library rate yields this plot's share of the aggregate cost.
  const isRuleC = isRuleCStep;
  const divisorByStep = isRuleC && numberOfPlots > 0 ? numberOfPlots : 1;

  // ── Rule-C short-circuit (Drainage / Plot Service / Service Connections).
  // Runs **before** the `if (!row) return []` guard below because the BoQ
  // summary items are aggregate-level (across all rows) — they don't need
  // a per-plot row. Per-plot share comes from `aggregate / numberOfPlots`.
  //
  // The Plot Matrix columns for these steps only carry one or two fields in
  // `fieldNames` (e.g. just `plots` for Plot Services), so iterating
  // `fieldNames` produces an incomplete resource list — the BoQ summary's
  // drainage / plotService sections enumerate the full set of resources
  // (every PPIC, gully, drainage channel, every plot-service field, etc.).
  // This branch pulls from `summaryItemsByCategory` instead so the central
  // repo task carries the complete resource list matching the BoQ summary.
  //
  // Category matching:
  //   • Plot Services / Service Connections — categoryName has shape
  //     `"<mainHeader> - <subHeader>"`, mainHeader matches the BoQ summary
  //     line's `category` ("Plot Services" / "Service Connections") directly.
  //   • Drainage — BoQ summary categories are `"OSMA-Foul"` / `"OSMA-Storm"`
  //     / `"Polypipe-Foul"` / `"Polypipe-Storm"`; Plot Matrix categoryName
  //     has shape `"Plot Drainage - Foul"` / `"Plot Drainage - Storm"`, so
  //     we match on whether the BoQ category includes the subHeader
  //     ("Foul" / "Storm").
  //
  // Each emitted resource carries:
  //   • `actualQty`  = `item.quantity / numberOfPlots` — per-plot share of
  //                    the BoQ summary's displayed quantity.
  //                    For drainage per-plot bend rows that's the plot
  //                    count's per-plot share (≈ 1); for drainage item rows
  //                    and plot service rows it's the per-plot share of the
  //                    raw take-off count.
  //   • `multiplier` = `item.rate / itemLibraryRate(code)` — recovers the
  //                    rate-inflation factor the BoQ row baked into its rate:
  //                      – Drainage per-plot rows: `ratePerPlot / dbRate`
  //                        (same as the payload multiplier — NOT forwarded
  //                        to summaryItemsByCategory; derived here via
  //                        the fallback for Plot Matrix).
  //                      – Plot Service apartments rows: the apartments
  //                        multiple (applied by
  //                        `applyPlotServiceQuantityMultipliers`).
  //                      – Ordinary item rows: 1.
  //                    Falls back to 1 when the item library rate is missing
  //                    or zero (e.g. NME PPIC's synthetic combined code
  //                    `GWALPDD018/GWALPDD019` — not present in the
  //                    `evaluateRateByCode` map) so the resource entry still
  //                    ships with `qty = actualQty`.
  //   • `qty`        = `actualQty × multiplier`. `qty × itemLibraryRate(code)`
  //                    matches the BoQ per-plot cost contribution.
  if (isRuleC && summaryItemsByCategory && summaryItemsByCategory.size > 0) {
    const parts = String(categoryName ?? '').split(' - ');
    const mainHeader = parts[0] ?? '';
    const subHeader = (parts[1] ?? '').toLowerCase();
    const collected: Array<{ code: string; quantity: number; rate: number; multiplier?: number }> = [];
    summaryItemsByCategory.forEach((items, category) => {
      if (stepName === 'Drainage') {
        if (subHeader && category.toLowerCase().includes(subHeader)) {
          collected.push(...items);
        }
      } else if (stepName === 'Plot Service' || stepName === 'Service Connections') {
        if (category === mainHeader) collected.push(...items);
      }
    });
    if (collected.length > 0) {
      // Per-item multiplier preference order:
      //   1. `item.multiplier` if the BoQ summary line already published one
      //      (e.g. plot-service apartments rows store the apartments multiple).
      //      Drainage per-plot rows do NOT forward their payload multiplier
      //      here — they always use path (2) below.
      //   2. `item.rate / itemLibraryRate(code)` derived fallback:
      //      – Drainage per-plot rows: `ratePerPlot / dbRate` (correct for
      //        `effectiveQty × dbRate = ratePerPlot`).
      //      – Plot Service apartments rows: apartments multiple.
      //      – Ordinary item rows: 1.
      //   3. 1 (identity) when the library rate is missing/zero so the entry
      //      still ships (e.g. NME PPIC's synthetic combined code).
      // Aggregation by code preserves the per-entry invariant
      // `qty = actualQty × multiplier` via `aggregate multiplier = qty / actualQty`.
      type Acc = { qty: number; actualQty: number };
      const byCode = new Map<string, Acc>();
      collected.forEach((item) => {
        const perPlotActualQty =
          numberOfPlots > 0 ? item.quantity / numberOfPlots : item.quantity;
        let rateMultiplier: number;
        if (
          typeof item.multiplier === 'number' &&
          Number.isFinite(item.multiplier) &&
          item.multiplier > 0
        ) {
          rateMultiplier = item.multiplier;
        } else {
          const libRate = itemLibraryRateByCode?.get(item.code.toUpperCase()) ?? 0;
          rateMultiplier =
            libRate > 0 && Number.isFinite(item.rate) && item.rate !== 0
              ? item.rate / libRate
              : 1;
        }
        const effectiveQty = perPlotActualQty * rateMultiplier;
        const existing = byCode.get(item.code) ?? { qty: 0, actualQty: 0 };
        byCode.set(item.code, {
          qty: existing.qty + effectiveQty,
          actualQty: existing.actualQty + perPlotActualQty,
        });
      });
      const out: { code: string; qty: number; actualQty: number; multiplier?: number }[] = [];
      byCode.forEach((acc, code) => {
        const multiplier = acc.actualQty !== 0 ? acc.qty / acc.actualQty : 1;
        out.push({ code, qty: acc.qty, actualQty: acc.actualQty, multiplier });
      });
      return out;
    }
  }

  // Skip the early `if (!row) return []` for 'Ret & Screen Walls' — that
  // step has cross-table sources (geoToberWallTable) that can supply
  // resources even when retainingWallsTable is empty for this plot. The
  // per-field loop below uses `row?.[field]` (optional chaining) for
  // same-table fields and `geoRowForRetainingWallsStep` / `cutFillRowForPlotDrivesStep`
  // for cross-table fields, so a missing `row` only suppresses the
  // same-table contributions, not the cross-table ones.
  if (!row && stepName !== 'Ret & Screen Walls') return [];

  // ── Garden: divide each field's qty by `occ` (the count of Plot Matrix
  // Garden columns that include it). Mirrors `computeGardenCellAmount`.
  const isGarden = stepName === 'Garden';

  // ── Per-field qty multiplier sourced from the BoQ summary payload's
  // `multiplier` field. Mirrors `BoqSummaryLineItem.multiplier` exactly:
  // whatever numeric multiplier the BoQ payload stores for a given field
  // (apartments multiple, retaining-walls `net × N`, etc.) is also baked
  // into the central-repo `resource[].qty` so `Σ resource.qty × rate(code)`
  // matches the BoQ amount (where the same multiplier is already in the
  // line's rate). The multiplier is also returned on each resource entry's
  // `multiplier` field so the central repo sees it as metadata alongside
  // the multiplied qty (matches what the BoQ summary payload sends).
  const getFieldQtyMultiplier = (field: string): number => {
    const m = multipliersByField?.get(field);
    return typeof m === 'number' && Number.isFinite(m) && m !== 0 ? m : 1;
  };

  // ── Retaining-walls build-up expansion. Wall-spec fields (`grw450`,
  // `drw900`, `ccrw1500`, …) don't have a single resource code in
  // `FIELD_RESOURCE_CODES` because each wall metre carries a build-up of
  // multiple sub-items (blockwork courses, facing-brick widths, BoE coping,
  // concrete, etc.) — each with its own item-library code. To match the BoQ
  // summary payload (which emits one child line per build-up sub-item under
  // each wall spec), this branch expands each wall-spec field into one
  // resource per build-up item. Without it, the central-repo Retaining
  // Walls stage only carries the screen-walls direct-code fields and silently
  // drops every build-up sub-item.
  //
  // Facing-brickwork uses `resolveWallsBuildUpItemCode(item.id, label)` so
  // the GWALPGW0020 / GWALPGW0021 dropdown choice picked in Configure
  // Headers is honoured; every other build-up id resolves via the static
  // `WALLS_BUILD_UP_ITEM_CODE_MAP`.
  const facingBrickworkLabel = (groundworks?.wallsBuildUpConfig as
    | { facingBrickworkLabel?: string }
    | undefined)?.facingBrickworkLabel;
  const specsById = new Map<string, RetainingWallsBuildUpSpecLike>();
  (retainingWallsBuildUpSpecs ?? []).forEach((spec) => {
    if (spec?.id) specsById.set(spec.id, spec);
  });
  // Geo / Tobermore wall spec lookup — same shape as retaining walls. Each
  // build-up item resolves its library code via either
  // `GEO_WALL_BUILD_UP_ITEM_CODE_MAP` or `TOBERMORE_BUILD_UP_ITEM_CODE_MAP`
  // depending on `spec.type` (matches the codeMap dispatch in
  // `computeGeoToberWallsSection`).
  const geoSpecsById = new Map<string, GeoToberWallBuildUpSpecLike>();
  (geoToberWallBuildUpSpecs ?? []).forEach((spec) => {
    if (spec?.id) geoSpecsById.set(spec.id, spec);
  });

  // Aggregate three values per code:
  //   • `qty`        — multiplied qty (`rawQty × multiplier / occ / divisor`).
  //                    `Σ qty × itemLibraryRate(code)` matches the BoQ amount.
  //   • `actualQty`  — pre-multiplier qty (`rawQty / occ / divisor`). Lets the
  //                    central repo see the original row quantity without
  //                    re-deriving it from `qty ÷ multiplier`.
  //   • `multiplier` — per-line multiplier from the BoQ summary; only set on
  //                    entries whose source field actually has one. First
  //                    non-1 multiplier wins when two fields somehow share a
  //                    code (no real-world case today).
  type AggregatedEntry = {
    code: string;
    qty: number;
    actualQty: number;
    multiplier?: number;
  };
  // Aggregate by `(code, multiplier)` so the per-entry invariant
  // `qty = actualQty × multiplier (if multiplier exists), else qty = actualQty`
  // holds even when several fields with the same code carry different
  // multipliers (e.g. multiple 100mm-blockwork courses under one wall spec
  // all map to GWALPS007 but each course count produces a distinct
  // `net × N`). For retaining-walls build-up entries, `multiplier` is the
  // composite `item.net × N` straight from the BoQ summary payload —
  // `net` is NOT exposed separately; it's baked into the multiplier.
  // Same-code-same-multiplier entries still aggregate, so non-multiplied
  // fields like Foundation Exc & Conc's `stripClaymaster` + `piledClaymaster`
  // (both GWALP022, both multiplier 1) collapse into one resource entry.
  const aggregated = new Map<string, AggregatedEntry>();
  const addAggregated = (
    code: string,
    qty: number,
    actualQty: number,
    multiplier: number,
    exposeMultiplier: boolean,
  ) => {
    const exposing = exposeMultiplier && multiplier !== 1;
    const key = exposing ? `${code}|${multiplier}` : code;
    const existing = aggregated.get(key);
    if (existing) {
      existing.qty += qty;
      existing.actualQty += actualQty;
      return;
    }
    const entry: AggregatedEntry = { code, qty, actualQty };
    if (exposing) entry.multiplier = multiplier;
    aggregated.set(key, entry);
  };

  // Wall build-up resources (retaining walls + geo/Tobermore) bypass
  // `addAggregated` and land here directly. Each build-up child gets its
  // OWN resource entry (one per `(spec, item)`) — the `(code, multiplier)`
  // aggregation key would corrupt `actualQty` when two build-up items in
  // the same spec share a code AND happen to share a composite multiplier
  // (e.g. multiple blockwork courses → GWALPS007 with the same `net × N`).
  // The aggregator's "actualQty += actualQty" would then double-count the
  // per-plot wall metres, even though every wall item on a given plot
  // shares the SAME takeoff `actualQty = wallQtyM`. Keeping wall entries
  // out of the aggregator preserves the per-plot invariant:
  //   • `actualQty` = THIS plot's wall metres (constant across all build-up
  //                  children of this spec — every item is "for this wall").
  //   • `multiplier` = `item.net × N` (per build-up item).
  //   • `qty`        = `actualQty × multiplier` (per build-up item).
  // Mirrors the BoQ summary's `summary.retainingWalls[spec].children[]` /
  // `summary.geoWalls[spec].children[]` shape — one row per build-up item,
  // no cross-item aggregation.
  const wallEntries: { code: string; qty: number; actualQty: number; multiplier?: number }[] = [];

  // Field list source. Use the **unfiltered** Plot Matrix categories from
  // `PLOT_MATRIX_STEP_CATEGORIES` rather than the column's already-filtered
  // `fieldNames` — `extractCategoryColumns` in
  // `plotMatrixCategoryGenerator.ts` drops fields whose key isn't present
  // on the step's primary table row (`expectedFieldName in transformedTable[*]`),
  // which silently removes legitimate cross-table fields:
  //   • `Ret & Screen Walls` column "Retaining Walls - Complete" — geo /
  //     Tobermore wall spec ids (`geoWallGPCW600` / `gpcw750` / `geoWallRW900`
  //     / `gpcw1050` / `tpcw1`-`tpcw8`) live on `geoToberWallTable`, NOT on
  //     `retainingWallsTable`, so the column filter drops them.
  //   • `Plot Drives` column "Private Drives to Binder - Sub Base" —
  //     `fillToMakeUp6F2` lives on `cutFillsTable`, NOT on `plotDrivesTable`,
  //     so the column filter drops it.
  // The Plot Matrix cell calc bypasses this by reading qtys directly from
  // the right table for these fields; the central-repo task resource list
  // now does the same. Iterating the original expected fields ensures the
  // cross-table lookup below sees every field that was intended to be in
  // this column, not just the subset that survived the table-presence filter.
  const stepCategories = PLOT_MATRIX_STEP_CATEGORIES[stepName];
  const expectedFromStepCategory = stepCategories?.[categoryName];
  const effectiveFieldNames =
    expectedFromStepCategory && expectedFromStepCategory.length > 0
      ? expectedFromStepCategory
      : fieldNames;

  // Cross-table row lookups. Some Plot Matrix columns aggregate fields that
  // live on different take-off tables than the column's `stepName` primary
  // table — without these lookups, `row[field]` reads `undefined` and the
  // field is silently dropped from the central-repo resource list.
  //
  //   • 'Ret & Screen Walls' step + geo / Tobermore wall spec id field
  //     (`geoWallGPCW600`, `gpcw750`, `geoWallRW900`, `gpcw1050`, `tpcw1`-`tpcw8`):
  //     qty lives on `geoToberWallTable`, not `retainingWallsTable`. Without
  //     this lookup every geo / Tobermore build-up resource silently drops.
  //   • 'Plot Drives' step + 'Private Drives to Binder - Sub Base' category +
  //     `fillToMakeUp6F2` field: qty lives on `cutFillsTable`, not
  //     `plotDrivesTable`. Mirrors the Plot Matrix cell calc's cross-source
  //     handler at `plotMatrixCategoryGenerator.ts:1791-1836` (which adds the
  //     6F2 contribution from Cut & Fills to the Sub Base column).
  // Derive the plot key from either the step's primary row (when present)
  // OR the explicit `plotNo` passed via context (always available from the
  // central-repo POST). The context fallback covers the case where the
  // step's primary table doesn't have a row for this plot but a
  // cross-source table does — most relevant for 'Ret & Screen Walls' plots
  // that only have geo / Tobermore wall data.
  const resolvedPlotKey = (() => {
    const fromRow = row?.plotNo;
    const fromCtx = contextPlotNo;
    const raw = String(fromRow ?? fromCtx ?? '').replace(/^[PG]-/i, '').trim();
    return raw;
  })();
  const lookupRowByPlotNo = (
    table: Array<Record<string, any>> | undefined,
  ): Record<string, any> | undefined => {
    if (!resolvedPlotKey || !table) return undefined;
    return table.find(
      (r) => String(r?.plotNo ?? '').replace(/^[PG]-/i, '').trim() === resolvedPlotKey,
    );
  };
  const geoRowForRetainingWallsStep =
    stepName === 'Ret & Screen Walls'
      ? lookupRowByPlotNo(
          groundworks?.geoToberWallTable as Array<Record<string, any>> | undefined,
        )
      : undefined;
  const cutFillRowForPlotDrivesStep =
    stepName === 'Plot Drives' && categoryName === 'Private Drives to Binder - Sub Base'
      ? lookupRowByPlotNo(
          groundworks?.cutFillsTable as Array<Record<string, any>> | undefined,
        )
      : undefined;

  effectiveFieldNames.forEach((field) => {
    // Source the row qty from the right table for cross-sourced fields;
    // otherwise use the step's primary row.
    const useGeoRow =
      stepName === 'Ret & Screen Walls' && field in geoToberWallFieldToSpecId;
    const useCutFillRow =
      stepName === 'Plot Drives' &&
      categoryName === 'Private Drives to Binder - Sub Base' &&
      field === 'fillToMakeUp6F2';
    const sourceRow = useGeoRow
      ? geoRowForRetainingWallsStep
      : useCutFillRow
        ? cutFillRowForPlotDrivesStep
        : row;
    const rawQty = Number(sourceRow?.[field]) || 0;
    if (!Number.isFinite(rawQty) || rawQty === 0) return;

    // Retaining-walls expansion path: field is a wall spec id (grw450 etc.).
    // Per-plot resources — for each build-up activity in this spec, emit
    // one resource entry with:
    //   • `actualQty` = THIS plot's wall metres (`rawQty / divisorByStep`),
    //                   NOT the total column qty. This is the takeoff value
    //                   for that build-up.
    //   • `multiplier` = `item.net × N` (composite) — `item.net` from THIS
    //                   spec's own persisted item (so multi-spec uses of
    //                   the same item id like `blockwork_100_0_220` don't
    //                   collide), `N = getWallsBuildUpRateMultiplier(itemId)`
    //                   (course count / half-brick width / BoE coping
    //                   multiple; 1 for items not in the multiplier map).
    //   • `qty`       = `actualQty × multiplier` = `wallQtyM × net × N`.
    //                   Matches the BoQ summary amount math
    //                   (`wallQtyM × (net × baseRate)` where `baseRate
    //                   = libraryRate × N`).
    //
    // Item source: prefer the persisted spec from
    // `retainingWallsBuildUpSpecs` (= `useGetRetainingWallsBuildUp`
    // response). If that's empty for the spec id (user hasn't saved
    // build-up via the API yet — `gw.wallsBuildUpDimensions` is the source
    // of truth), fall back to `wallsBuildUpDimensions[specId]` matched
    // against the mock spec's items by description (mirrors the BoQ
    // summary computer's two-branch resolution in
    // `computeRetainingWallsSection` → fallback branch at line 658+).
    // Without this fallback the central-repo task ships `resource: []`
    // whenever the API hook hasn't returned (or no spec was ever saved
    // through it), even though `wallsBuildUpDimensions` has data and the
    // BoQ summary renders correctly.
    const specId = wallFieldToSpecId[field];
    const persistedSpec = specId ? specsById.get(specId) : undefined;
    let resolvedItems: Array<{ id: string; description: string; net: number }> = [];
    if (persistedSpec?.items && persistedSpec.items.length > 0) {
      resolvedItems = persistedSpec.items
        .filter((i) => i && typeof i === 'object')
        .map((i) => ({
          id: String(i.id ?? ''),
          description: String(i.description ?? ''),
          net:
            typeof i.net === 'number' && Number.isFinite(i.net)
              ? i.net
              : 0,
        }));
    } else if (specId) {
      // Fallback path: build items list from the mock `wallSpecifications`
      // (which carries every default item id + description for each spec)
      // and overlay the user's per-item `net` from `wallsBuildUpDimensions`
      // (state-derived, populated on fresh import or API hydration).
      const mockSpec = wallSpecifications.find((s) => s.id === specId);
      if (mockSpec) {
        const dims =
          (groundworks?.wallsBuildUpDimensions as
            | Record<string, Array<{ description?: string; net?: number }>>
            | undefined)?.[specId] ?? [];
        const dimsByDesc = new Map<string, number>();
        dims.forEach((d) => {
          const desc = String(d?.description ?? '').trim().toLowerCase();
          if (!desc) return;
          const v =
            typeof d?.net === 'number' && Number.isFinite(d.net)
              ? d.net
              : 0;
          // First write wins per spec (matches BoQ fallback's iteration).
          if (!dimsByDesc.has(desc)) dimsByDesc.set(desc, v);
        });
        resolvedItems = mockSpec.items.map((i) => ({
          id: i.id,
          description: i.description,
          net:
            dimsByDesc.get(i.description.toLowerCase()) ??
            (typeof i.net === 'number' && Number.isFinite(i.net) ? i.net : 0),
        }));
      }
    }
    if (specId && resolvedItems.length > 0) {
      resolvedItems.forEach((item) => {
        const itemId = item.id;
        if (!itemId) return;
        const desc = String(item.description ?? '').trim();
        // Match `computeRetainingWallsSection`'s item filter (skip empty /
        // numeric-only descriptions used as section spacers).
        if (!desc || /^[0-9.]+$/.test(desc)) return;
        const code = resolveWallsBuildUpItemCode(itemId, facingBrickworkLabel)
          ?? WALLS_BUILD_UP_ITEM_CODE_MAP[itemId];
        if (!code) return;
        const itemNet =
          typeof item.net === 'number' && Number.isFinite(item.net)
            ? Math.abs(item.net)
            : 0;
        const rateMultiplier = getWallsBuildUpRateMultiplier(itemId);
        const multiplier = itemNet * rateMultiplier;
        // Emit even when multiplier is 0 (unconfigured build-up activity
        // with `net = 0`) so the central repo sees the full build-up
        // enumeration matching the BoQ summary's children list.
        const wallQtyM = rawQty / divisorByStep;
        const actualQty = wallQtyM;
        const qty = actualQty * multiplier;
        // Push directly to wallEntries — one entry per build-up item.
        // Bypass `addAggregated` because the per-spec invariant
        // `actualQty = wallQtyM` (constant) would be broken by the
        // aggregator's "actualQty += actualQty" when two items in the
        // same spec share `(code, multiplier)`.
        const entry: { code: string; qty: number; actualQty: number; multiplier?: number } = {
          code,
          qty,
          actualQty,
        };
        if (multiplier !== 1) entry.multiplier = multiplier;
        wallEntries.push(entry);
      });
      return;
    }

    // Geo / Tobermore wall expansion — mirrors the retaining-walls branch
    // above but for the GPCW / GeoWall RW / TPCW columns. Each build-up
    // item resolves its library code via either GEO_WALL_BUILD_UP_ITEM_CODE_MAP
    // or TOBERMORE_BUILD_UP_ITEM_CODE_MAP based on `spec.type` (matches
    // `computeGeoToberWallsSection`'s codeMap dispatch). Geo/Tobermore lines
    // store `multiplier: net` in the BoQ summary (no rate multiplier `N`
    // beyond 1), so the per-entry multiplier here is just `item.net` — same
    // shape as the BoQ payload.
    const geoSpecId = geoToberWallFieldToSpecId[field];
    const geoSpec = geoSpecId ? geoSpecsById.get(geoSpecId) : undefined;
    if (geoSpec && geoSpec.items && geoSpec.items.length > 0) {
      const codeMap = geoSpec.type === 'geowall'
        ? GEO_WALL_BUILD_UP_ITEM_CODE_MAP
        : TOBERMORE_BUILD_UP_ITEM_CODE_MAP;
      geoSpec.items.forEach((item) => {
        const itemId = item?.id;
        if (!itemId) return;
        const desc = String(item?.description ?? '').trim();
        if (!desc || /^[0-9.]+$/.test(desc)) return;
        const code = codeMap[itemId];
        if (!code) return;
        // Per-plot resource — `actualQty = wallQtyM` (this plot's takeoff
        // metres of this geo/Tobermore wall spec). Geo / Tobermore wall
        // items have no rate multiplier `N` beyond 1, so `multiplier =
        // item.net` (just the per-metre net), and `qty = actualQty ×
        // multiplier`. Same shape as the BoQ summary payload's
        // `multiplier: net` for geo / Tobermore lines. `item.net` is read
        // from THIS spec's own persisted item to avoid the shared
        // `multipliersByField` first-write-wins corruption when the same
        // build-up item id appears in multiple geo/Tobermore specs.
        const multiplier =
          typeof item?.net === 'number' && Number.isFinite(item.net)
            ? Math.abs(item.net)
            : 0;
        // Emit even when multiplier is 0 (unconfigured build-up activity)
        // so the central repo sees the full build-up enumeration matching
        // the BoQ summary's children list.
        const wallQtyM = rawQty / divisorByStep;
        const actualQty = wallQtyM;
        const qty = actualQty * multiplier;
        // Push directly to wallEntries (see retaining-walls branch above
        // for the rationale — avoids the aggregator double-counting
        // `actualQty` when two geo/Tobermore items share a code and
        // happen to share `item.net`).
        const entry: { code: string; qty: number; actualQty: number; multiplier?: number } = {
          code,
          qty,
          actualQty,
        };
        if (multiplier !== 1) entry.multiplier = multiplier;
        wallEntries.push(entry);
      });
      return;
    }

    // Standard per-field path — prefer the BoQ-summary-resolved code so
    // `[dynamic]` and empty `FIELD_RESOURCE_CODES` entries (mesh type,
    // concrete grade, etc.) still emit a resource. Fall back to the
    // static map when the field didn't reach the BoQ summary (rare —
    // happens only for fields with zero qty across every plot, in which
    // case the rowQty check above already skipped this iteration).
    const code = codesByField?.get(field) ?? FIELD_RESOURCE_CODES[field];
    if (!code || code === '[dynamic]') return;
    const occ = isGarden ? GARDEN_FIELD_OCCURRENCE.get(field) ?? 1 : 1;
    const actualQty = rawQty / occ / divisorByStep;
    // Only thickness fields (M³-conversion thickness from the BoQ summary)
    // multiply qty by the multiplier in the standard branch. Apartments /
    // service-connection-apartments still appear in the multiplier map but
    // are not multiplied here (per the user's "multiplier should only be
    // included where the activities have thickness" guidance, scoped to the
    // standard per-field path). The retaining-walls expansion above
    // separately exposes its `net × N` multiplier because the BoQ summary
    // payload sends one per build-up sub-item — see that branch.
    const hasThickness = thicknessFields?.has(field) ?? false;
    const qtyMultiplier = hasThickness ? getFieldQtyMultiplier(field) : 1;
    const effectiveQty = actualQty * qtyMultiplier;
    addAggregated(code, effectiveQty, actualQty, qtyMultiplier, /* exposeMultiplier */ hasThickness);
  });
  // Emit every aggregated entry — wall-branch build-up activities with
  // `multiplier = 0` (unconfigured spec rows where `item.net = 0`) deliberately
  // ship with `qty = 0` so the full build-up enumeration reaches the central
  // repo. Standard-branch and soil-management-branch entries already filter
  // `rawQty === 0` / `cellTotal === 0` upstream, so they never produce
  // `qty = 0` entries in practice.
  const out: { code: string; qty: number; actualQty: number; multiplier?: number }[] = [];
  // Wall build-up resources first (one entry per build-up child, no
  // cross-item aggregation) — preserves the per-plot `actualQty = wallQtyM`
  // invariant. See `wallEntries` declaration above for the rationale.
  wallEntries.forEach((entry) => {
    out.push(entry);
  });
  // Standard-branch entries (Foundations, Cut Fills, Plot Drives, Garden,
  // Scaffold, etc.) — aggregated by `(code, multiplier)` since per-field
  // contributions are linear in qty and actualQty (qty = actualQty ×
  // multiplier holds under summation when both sides scale linearly).
  aggregated.forEach((entry) => {
    out.push(entry);
  });
  return out;
}

// ─── Special-case formula definitions ─────────────────────────────────────────
// Only categories with non-obvious calculation logic (direct item library
// evaluate-code lookup or build-up–derived netPerM) get explicit definitions.
// All other categories use buildGenericDefinition.

const FORMULA_DEFINITIONS: Record<string, FormulaDefinition> = {
  'Plot Drainage - Foul': {
    summary:
      'BoQ formula (DrainageBillSummary FOUL section, calculateDrainageFoulTotal):\n' +
      '  • Route: Polypipe if any polypipe data and NO OSMA data; otherwise OSMA.\n' +
      '  • combinedSum = Σ_field (sumField(field) × rate(field)) over all OSMA_FOUL_COMBINED_FIELDS\n' +
      '    (osma32mmBend, osmaPPIC, osma50mmBend … tipOnSite — 15 fields)\n' +
      '  • ratePerPlot = combinedSum ÷ foulNrPlotsTakeOff\n' +
      '  • labAmount = Math.round(foulTotalPlots) × parseFloat(ratePerPlot.toFixed(2))\n' +
      '  • foulTotal = labAmount + nmePpicAmt + 600mmNmePpicAmt\n' +
      'Plot Matrix cell = foulTotal ÷ numberOfPlots (distributed evenly across plots).\n' +
      '  1 plot → cell = bill summary Foul section total exactly.\n' +
      '  N plots → cell = Foul total ÷ N.',
    parts: [
      {
        label: 'OSMA route (default — when OSMA data exists)',
        rows: [
          {
            key: 'osma32mmBend',
            columnHeader: 'Per-plot section (all 15 OSMA_FOUL_COMBINED_FIELDS aggregated)',
            fieldName: 'osma32mmBend',
            resourceCode: 'GWALPDD001A',
            rateSource: 'Item library',
            note: 'Σ_combinedFields(qty×rate) ÷ foulNrPlotsTakeOff × Math.round(foulTotalPlots) ÷ numberOfPlots',
          },
          {
            key: 'osmaNmePpic',
            columnHeader: 'OSMA NME PPIC',
            fieldName: 'osmaNmePpic',
            resourceCode: 'GWALPDD018',
            rateSource: 'Item library',
            note: 'qty × rate',
          },
          {
            key: 'osma600mmNmePpic',
            columnHeader: 'OSMA 600mm NME PPIC',
            fieldName: 'osma600mmNmePpic',
            resourceCode: '—',
            rateSource: 'BOQ rate',
            note: 'qty × rate',
          },
        ],
      },
      {
        label: 'Polypipe route (only when Polypipe data exists AND no OSMA data)',
        rows: [
          {
            key: 'polypipe32mmBend',
            columnHeader: 'Per-plot section (all 15 POLYPIPE_FOUL_COMBINED_FIELDS aggregated)',
            fieldName: 'polypipe32mmBend',
            resourceCode: 'GWALPDD001A',
            rateSource: 'Item library',
            note: 'Σ_combinedFields(qty×rate) ÷ ppNrPlotsTakeOff × Math.round(ppTotalPlots) ÷ numberOfPlots',
          },
          {
            key: 'polypipeNmePpic',
            columnHeader: 'Polypipe NME PPIC',
            fieldName: 'polypipeNmePpic',
            resourceCode: 'GWALPDD018',
            rateSource: 'Item library',
            note: 'qty × rate',
          },
          {
            key: 'polypipe600mmNmePpic',
            columnHeader: 'Polypipe 600mm NME PPIC',
            fieldName: 'polypipe600mmNmePpic',
            resourceCode: '—',
            rateSource: 'BOQ rate',
            note: 'qty × rate',
          },
        ],
      },
    ],
  },

  'Plot Drainage - Storm': {
    summary:
      'BoQ formula (DrainageBillSummary STORM section, calculateDrainageStormTotal):\n' +
      '  • Route: Polypipe if any polypipe data and NO OSMA data; otherwise OSMA.\n' +
      '  • combinedSum = Σ_field (sumField(field) × rate(field)) over all OSMA_STORM_COMBINED_FIELDS\n' +
      '    (osmaStorm32mmBend, osmaStorPPIC, osmaStorm50mmBend … osmaStormTipOnsite — 14 fields)\n' +
      '  • ratePerPlot = combinedSum ÷ foulNrPlotsTakeOff\n' +
      '  • labAmount = Math.round(foulTotalPlots) × parseFloat(ratePerPlot.toFixed(2))\n' +
      '  • stormTotal = labAmount + nmePpicAmt + 600mmNmePpicAmt + yardGullyAmt + drainageChannelAmt + landDrainAmt\n' +
      '  • For yardGully/drainageChannel/landDrain: storm-specific qty if > 0, else falls back to generic field qty.\n' +
      'Plot Matrix cell = stormTotal ÷ numberOfPlots (distributed evenly across plots).\n' +
      '  1 plot → cell = bill summary Storm section total exactly.\n' +
      '  N plots → cell = Storm total ÷ N.',
    parts: [
      {
        label: 'OSMA route (default — when OSMA data exists)',
        rows: [
          {
            key: 'osmaStorm32mmBend',
            columnHeader: 'Per-plot section (all 14 OSMA_STORM_COMBINED_FIELDS aggregated)',
            fieldName: 'osmaStorm32mmBend',
            resourceCode: 'GWALPDD001A',
            rateSource: 'Item library',
            note: 'Σ_combinedFields(qty×rate) ÷ foulNrPlotsTakeOff × Math.round(foulTotalPlots) ÷ numberOfPlots',
          },
          {
            key: 'osmaStormNmePpic',
            columnHeader: 'OSMA Storm NME PPIC',
            fieldName: 'osmaStormNmePpic',
            resourceCode: 'GWALPDD018',
            rateSource: 'Item library',
            note: 'qty × rate',
          },
          {
            key: 'osmaStorm600mmNmePpic',
            columnHeader: 'OSMA Storm 600mm NME PPIC',
            fieldName: 'osmaStorm600mmNmePpic',
            resourceCode: '—',
            rateSource: 'BOQ rate',
            note: 'qty × rate',
          },
          {
            key: 'osmaStormYardGully',
            columnHeader: 'Yard Gully (OSMA Storm)',
            fieldName: 'osmaStormYardGully',
            resourceCode: 'GWALPDD051',
            rateSource: 'Item library',
            note: 'qty (or fallback yardGully) × rate',
          },
          {
            key: 'osmaStormDrainageChannel',
            columnHeader: 'Drainage Channel (OSMA Storm)',
            fieldName: 'osmaStormDrainageChannel',
            resourceCode: '[dynamic]',
            rateSource: 'Item library (dynamic)',
            note: 'qty (or fallback drainageChannel) × rate',
          },
          {
            key: 'osmaStormLandDrain',
            columnHeader: 'Land Drain (OSMA Storm)',
            fieldName: 'osmaStormLandDrain',
            resourceCode: '[dynamic]',
            rateSource: 'Item library (dynamic)',
            note: 'qty (or fallback landDrain) × rate',
          },
        ],
      },
      {
        label: 'Polypipe route (only when Polypipe data exists AND no OSMA data)',
        rows: [
          {
            key: 'polypipeStorm32mmBend',
            columnHeader: 'Per-plot section (all 14 POLYPIPE_STORM_COMBINED_FIELDS aggregated)',
            fieldName: 'polypipeStorm32mmBend',
            resourceCode: 'GWALPDD001A',
            rateSource: 'Item library',
            note: 'Σ_combinedFields(qty×rate) ÷ ppNrPlotsTakeOff × Math.round(ppTotalPlots) ÷ numberOfPlots',
          },
          {
            key: 'polypipeStormNmePpic',
            columnHeader: 'Polypipe Storm NME PPIC',
            fieldName: 'polypipeStormNmePpic',
            resourceCode: 'GWALPDD018',
            rateSource: 'Item library',
            note: 'qty × rate',
          },
          {
            key: 'polypipeStorm600mmNmePpic',
            columnHeader: 'Polypipe Storm 600mm NME PPIC',
            fieldName: 'polypipeStorm600mmNmePpic',
            resourceCode: '—',
            rateSource: 'BOQ rate',
            note: 'qty × rate',
          },
          {
            key: 'polypipeStormYardGully',
            columnHeader: 'Yard Gully (Polypipe Storm)',
            fieldName: 'polypipeStormYardGully',
            resourceCode: 'GWALPDD051',
            rateSource: 'Item library',
            note: 'qty (or fallback yardGully) × rate',
          },
          {
            key: 'polypipeStormDrainageChannel',
            columnHeader: 'Drainage Channel (Polypipe Storm)',
            fieldName: 'polypipeStormDrainageChannel',
            resourceCode: '[dynamic]',
            rateSource: 'Item library (dynamic)',
            note: 'qty (or fallback drainageChannel) × rate',
          },
          {
            key: 'polypipeStormLandDrain',
            columnHeader: 'Land Drain (Polypipe Storm)',
            fieldName: 'polypipeStormLandDrain',
            resourceCode: '[dynamic]',
            rateSource: 'Item library (dynamic)',
            note: 'qty (or fallback landDrain) × rate',
          },
        ],
      },
    ],
  },

  'Plot Services - Plot Services': {
    summary:
      'BoQ formula (PlotServiceBillSummary — Plot Services section, calculatePlotServicesSectionTotal):\n' +
      '  plotServicesTotal = Σ_fields (qty × rate)\n' +
      'Plot Matrix cell = plotServicesTotal ÷ numberOfPlots (distributed evenly across plots; rates from item library).',
    parts: [
      {
        rows: [
          { key: 'plots',                  columnHeader: 'Plots — plot services',         fieldName: 'plots',                  resourceCode: 'GWALPDS006',  rateSource: 'Item library', note: 'qty × rate' },
          { key: 'apartments',             columnHeader: 'Apartments',                    fieldName: 'apartments',             resourceCode: 'GWALPDS006',  rateSource: 'Item library', note: 'qty × rate × rateMultiplier' },
          { key: 'eo25mmBarrierPipe',      columnHeader: 'EO 25mm Barrier Pipe',          fieldName: 'eo25mmBarrierPipe',      resourceCode: 'GWALPDS010',  rateSource: 'Item library', note: 'qty × rate' },
          { key: 'eo32mmBarrierPipe',      columnHeader: 'EO 32mm Barrier Pipe',          fieldName: 'eo32mmBarrierPipe',      resourceCode: 'GWALPDS011',  rateSource: 'Item library', note: 'qty × rate' },
          { key: 'virginMedia',            columnHeader: 'Virgin Media duct',             fieldName: 'virginMedia',            resourceCode: 'GWALPDS007A', rateSource: 'Item library', note: 'qty × rate' },
          { key: 'garageDuct',             columnHeader: 'Garage Duct',                   fieldName: 'garageDuct',             resourceCode: 'GWALPDS004',  rateSource: 'Item library', note: 'qty × rate' },
          { key: 'privateStLightDuct',     columnHeader: 'Private Street Lighting Duct',  fieldName: 'privateStLightDuct',     resourceCode: 'GWALPDS005',  rateSource: 'Item library', note: 'qty × rate' },
          { key: 'pccEdgingsGasBoxes',     columnHeader: 'PCC Edgings to Gas Boxes',      fieldName: 'pccEdgingsGasBoxes',     resourceCode: 'GWALPDS009',  rateSource: 'Item library', note: 'qty × rate' },
          { key: 'ductingToChargingPoint', columnHeader: 'Ducting to EV Charging Point',  fieldName: 'ductingToChargingPoint', resourceCode: 'GWALPDS005',  rateSource: 'Item library', note: 'qty × rate' },
          { key: 'concBaseChargingPoints', columnHeader: 'Concrete Base — Charging Pts',  fieldName: 'concBaseChargingPoints', resourceCode: 'GWALPDS015',  rateSource: 'Item library', note: 'qty × rate' },
          { key: 'installBollardBase',     columnHeader: 'Install Bollard Base',          fieldName: 'installBollardBase',     resourceCode: 'GWALPDS016',  rateSource: 'Item library', note: 'qty × rate' },
          { key: 'airSourceHeatPump',      columnHeader: 'Air Source Heat Pump',          fieldName: 'airSourceHeatPump',      resourceCode: 'GWALPDS017',  rateSource: 'Item library', note: 'qty × rate' },
        ],
      },
    ],
  },

  'Service Connections - Pit Excavation': {
    summary:
      'BoQ formula (PlotServiceBillSummary — Service Connections section, calculateServiceConnectionsGrandTotal):\n' +
      '  serviceConnectionsTotal = Σ_fields (qty × rate)\n' +
      'Plot Matrix cell = serviceConnectionsTotal ÷ numberOfPlots (distributed evenly across plots; rates from item library).',
    parts: [
      {
        rows: [
          { key: 'serviceConnectionPlots',      columnHeader: 'Plots — service connections',      fieldName: 'serviceConnectionPlots',      resourceCode: 'GWALPDS007', rateSource: 'Item library', note: 'qty × rate' },
          { key: 'serviceConnectionApartments', columnHeader: 'Apartments — service connections', fieldName: 'serviceConnectionApartments', resourceCode: 'GWALPDS007', rateSource: 'Item library', note: 'qty × rate × rateMultiplier' },
          { key: 'vacEx',                       columnHeader: 'Vacuum Excavation',                fieldName: 'vacEx',                       resourceCode: 'GWALPDS003', rateSource: 'Item library', note: 'qty × rate' },
        ],
      },
    ],
  },

  'Private Drives to Binder - Sub Base': {
    summary:
      'Plot Matrix cell = Σ (per-field proportional Bill Summary share).\n' +
      '  • Plot Drives fields → share of the Plot Drives Bill Summary line (rate from item library + m³→m² thickness multiplier baked in).\n' +
      '  • `fillToMakeUp6F2` → share of the Cut & Fill Bill Summary "Cut & Fill to Drives" line; quantity is sourced from `cutFillsTable` and the rate from item library code `GWALP004`. This field is cross-sourced because it lives in the Cut & Fills take-off but bills under Private Drives → Sub Base.',
    parts: [
      {
        label: 'Cross-sourced from Cut & Fills',
        rows: [
          row('fillToMakeUp6F2', 'Item library', 'GWALP004', 'qty from cutFillsTable · BoQ source: Cut & Fill Bill Summary'),
        ],
      },
      {
        label: 'Plot Drives fields (qty from plotDrivesTable · BoQ source: Plot Drives Bill Summary)',
        rows: [
          'macadamLevelCompact',
          'macadamGeotextileLayer',
          'macadamSubBase',
          'blockLevelCompact',
          'blockGeotextileLayer',
          'block300mmAdditionalCapping',
          'permeableLevelCompact',
          'permeableImpermeableGeotextileLayer',
          'permeable10to63mmCleanAggregateSubBase',
          'permeable5to20mmCleanAggregateSubBase',
          'permeableGeotextileLayer',
        ].map((f) => row(f, fieldRateSource(f), undefined, 'qty × rate (proportional share)')),
      },
    ],
  },

  'Screen Walls - upto DPC': {
    summary: 'Item library direct lookup (Evaluate codes) — qty × rate[code]',
    parts: [
      {
        rows: [
          row('screenWalls',   'Item library', 'GWALPGW003',  'linear m'),
          row('screenWallsEO', 'Item library', 'GWALPGW003A', 'EO pillars, Nr'),
          row('screenWallsAdditionalExcavation', 'Item library', 'GWALPGW0026', 'Additional Excavation, m3'),
          row('screenWallsAdditionalConcrete',   'Item library', 'GWALPGW0015', 'Additional Concrete, m3'),
        ],
      },
    ],
  },

  'Retaining Walls - Complete': {
    summary: 'Part 1: item library direct lookup · Part 2: qty × build-up netPerM × (1 + OHP%)',
    parts: [
      {
        label: 'Part 1 — screen wall items (item library direct lookup)',
        rows: [
          row('screenWallsSof',         'Item library', 'GWALPGW0043', 'SoE 450mm, m'),
          row('screenWallsLog',         'Item library', 'ACL012A',     'Log 1200mm, m'),
          row('screenWallsGravelBoard', 'Item library', 'GWALPGW0023',  'Gravel board, m'),
        ],
      },
      {
        label: 'Part 2 — Garden Retaining Walls · qty × build-up netPerM × (1 + OHP%)',
        rows: ['grw450','grw600','grw750','grw900','grw1050','grw1200','grw1500','grw1800','grw2000']
          .map((f) => row(f, 'Build-up spec')),
      },
      {
        label: 'Part 2 — Drive Retaining Walls · qty × build-up netPerM × (1 + OHP%)',
        rows: ['drw450','drw600','drw750','drw900','drw1050','drw1200','drw1500','drw1800','drw2000','drw2250','drw2500','drw2750','drw3000']
          .map((f) => row(f, 'Build-up spec')),
      },
      {
        label: 'Part 2 — Concrete Core Retaining Walls · qty × build-up netPerM × (1 + OHP%)',
        rows: ['ccrw1500','ccrw1800','ccrw2000','ccrw2250','ccrw2500','ccrw2750','ccrw3000','ccrw3250','ccrw3500']
          .map((f) => row(f, 'Build-up spec')),
      },
      {
        label: 'Part 2 — Geo Walls (GPCW) · qty × build-up netPerM × (1 + OHP%)',
        rows: ['geoWallGPCW600','gpcw750','geoWallRW900','gpcw1050']
          .map((f) => row(f, 'Build-up spec')),
      },
      {
        label: 'Part 2 — Tobermore Walls (TPCW) · qty × build-up netPerM × (1 + OHP%)',
        rows: ['tpcw1','tpcw2','tpcw3','tpcw4','tpcw5','tpcw6','tpcw7','tpcw8']
          .map((f) => row(f, 'Build-up spec')),
      },
    ],
  },
};

// ─── Generic formula: auto-derived from fieldNames ────────────────────────────

function fieldRateSource(field: string): string {
  const code = FIELD_RESOURCE_CODES[field];
  if (code === undefined) return 'BOQ rate';
  if (code === '') return 'BOQ rate';
  if (code === '[dynamic]') return 'Item library (dynamic)';
  return 'Item library';
}

function buildGenericDefinition(fieldNames: string[]): FormulaDefinition {
  return {
    summary: 'qty × rate for each field',
    parts: [
      {
        rows: fieldNames.map((f) => row(f, fieldRateSource(f))),
      },
    ],
  };
}

// ─── Soil management — dynamic (reflects the active Configure Headers pick) ───
// For Topsoil Disposal / Disposal Of Surplus / Import of Deficit, the rate
// code is not a fixed constant — it comes from the user's selection on the
// Cut & Fills Configure Headers modal (management + subOption + disposal
// classification for hazardous subsoil). We surface only the code that's
// actually being used to compute the cell, not every possible permutation.

type SoilMgmt = {
  management?: string;       // 'Surplus' | 'Deficit' | ''
  subOption?: string;
  disposalClassification?: string;
  code?: string;             // resolved on Configure Headers apply
};

function activeSubOptionLabel(mgmt: SoilMgmt | undefined): string {
  if (!mgmt?.management) return '— not configured —';
  const parts = [mgmt.management, mgmt.subOption].filter(Boolean);
  if (mgmt.disposalClassification) parts.push(mgmt.disposalClassification);
  return parts.join(' · ');
}

function buildTopsoilDisposalDefinition(groundworks: any): FormulaDefinition {
  const mgmt: SoilMgmt = groundworks?.topsoilManagement ?? {};
  const code = mgmt.code || '';
  const isExcluded = !code || code.toUpperCase() === 'NONE';
  return {
    summary:
      'BoQ formula (GroundworksCutFillBillSummary — Section 3.1, computeSoilBoQTotals):\n' +
      '  topsoilAmount = | Σ topsoilDisposalOnsite − Σ topsoilDisposalOffsite | × topsoilRate\n' +
      'Plot Matrix cell = topsoilAmount ÷ numberOfPlots (Rule C — equal split).',
    parts: [
      {
        label: 'Active activity (from Configure Headers → Topsoil Management)',
        rows: [
          {
            key: 'topsoil-active',
            columnHeader: activeSubOptionLabel(mgmt),
            fieldName: 'topsoilManagement.code',
            resourceCode: isExcluded ? 'None' : code,
            rateSource: isExcluded ? 'BOQ rate' : 'Item library',
            note: isExcluded ? 'rate = 0 (excluded / no charge)' : 'topsoilRate = itemLibrary[code].nettRate',
          },
        ],
      },
    ],
  };
}

function buildSubsoilDefinition(
  groundworks: any,
  expectedManagement: 'Surplus' | 'Deficit',
): FormulaDefinition {
  const mgmt: SoilMgmt = groundworks?.subsoilManagement ?? {};
  const active = mgmt.management === expectedManagement;
  const code = active ? (mgmt.code || '') : '';
  const isExcluded = active && (!code || code.toUpperCase() === 'NONE');
  const otherSide = expectedManagement === 'Surplus' ? 'Import of Deficit' : 'Disposal Of Surplus';
  const amountName = expectedManagement === 'Surplus' ? 'disposalAmount' : 'importAmount';

  return {
    summary:
      'BoQ formula (GroundworksCutFillBillSummary — Section 3.2, computeSoilBoQTotals):\n' +
      '  subsoilQty   = | Σ_8pairs cut − Σ_8pairs fill |  (8 areas: foundations, slabPaths, gardens, drives, foundationsArisings, plotDrainage, walls, plotServices)\n' +
      '  subsoilAmt   = subsoilQty × subsoilRate\n' +
      `  ${amountName} = (subsoilManagement.management === "${expectedManagement}") ? subsoilAmt : 0\n` +
      `Plot Matrix cell = ${amountName} ÷ numberOfPlots (Rule C — equal split).\n` +
      `Mutually exclusive with "${otherSide}": only one side is non-zero per take-off, driven by the Surplus/Deficit dropdown on Configure Headers.`,
    parts: [
      {
        label: active
          ? 'Active activity (from Configure Headers → Subsoil Management)'
          : `Inactive — current Configure Headers pick is "${mgmt.management || 'not configured'}" (this column is zero in the matrix)`,
        rows: active
          ? [
              {
                key: 'subsoil-active',
                columnHeader: activeSubOptionLabel(mgmt),
                fieldName: 'subsoilManagement.code',
                resourceCode: isExcluded ? 'None' : code,
                rateSource: isExcluded ? 'BOQ rate' : 'Item library',
                note: isExcluded ? 'rate = 0 (excluded / no charge)' : 'subsoilRate = itemLibrary[code].nettRate',
              },
            ]
          : [],
      },
    ],
  };
}

function buildScreenWallsUptoDpcDefinition(groundworks?: any): FormulaDefinition {
  const screenWallsLabel = groundworks?.wallsBuildUpConfig?.screenWallsLabel as string | undefined;
  const eoPillarLabel = groundworks?.wallsBuildUpConfig?.eoPillarLabel as string | undefined;
  const screenWallsSuffix = screenWallsLabel === SCREEN_WALLS_M_J_SUPPLY_LABEL ? 'M&J Supply' : 'Free Issue';
  const eoPillarSuffix = eoPillarLabel === EO_PILLAR_M_J_SUPPLY_LABEL ? 'M&J Supply' : 'Free Issue';
  const screenWallsCode = resolveScreenWallsCode(screenWallsLabel);
  const screenWallsEOCode = resolveEOPillarCode(eoPillarLabel);
  return {
    summary: `Item library direct lookup (Evaluate codes) — qty × rate[code]\nScreen Walls supply type (Configure Headers): ${screenWallsSuffix} · EO Pillar supply type: ${eoPillarSuffix}`,
    parts: [
      {
        rows: [
          {
            key: 'screenWalls',
            columnHeader: `Screen Walls - ${screenWallsSuffix}`,
            fieldName: 'screenWalls',
            resourceCode: screenWallsCode,
            rateSource: 'Item library',
            note: 'linear m',
          },
          {
            key: 'screenWallsEO',
            columnHeader: `EO Pillar - ${eoPillarSuffix}`,
            fieldName: 'screenWallsEO',
            resourceCode: screenWallsEOCode,
            rateSource: 'Item library',
            note: 'EO pillars, Nr',
          },
          {
            key: 'screenWallsAdditionalExcavation',
            columnHeader: 'Screen Walls Additional Excavation',
            fieldName: 'screenWallsAdditionalExcavation',
            resourceCode: 'GWALPGW0026',
            rateSource: 'Item library',
            note: 'Additional Excavation, m3',
          },
          {
            key: 'screenWallsAdditionalConcrete',
            columnHeader: 'Screen Walls Additional Concrete',
            fieldName: 'screenWallsAdditionalConcrete',
            resourceCode: 'GWALPGW0015',
            rateSource: 'Item library',
            note: 'Additional Concrete, m3',
          },
        ],
      },
    ],
  };
}

const MODAL_TABLE_COLUMNS = [
  {
    title: 'Column Header',
    dataIndex: 'columnHeader' as keyof FormulaRow,
    key: 'columnHeader',
    render: (v: string) => <span style={{ fontWeight: 500 }}>{v}</span>,
  },
  {
    title: 'Resource Code',
    dataIndex: 'resourceCode' as keyof FormulaRow,
    key: 'resourceCode',
    width: 150,
    render: (v: string) =>
      v ? (
        <span style={{ fontFamily: 'monospace', color: '#059669', fontWeight: 600 }}>{v}</span>
      ) : (
        <span style={{ color: '#d1d5db' }}>—</span>
      ),
  },
  {
    title: 'Rate',
    dataIndex: 'rate' as keyof FormulaRow,
    key: 'rate',
    width: 100,
    align: 'right' as const,
    render: (v: number | undefined) =>
      v !== undefined && Number.isFinite(v) ? (
        <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#1d4ed8' }}>
          £{v.toFixed(2)}
        </span>
      ) : (
        <span style={{ color: '#d1d5db' }}>—</span>
      ),
  },
  // {
  //   title: 'Modified Rate',
  //   dataIndex: 'modifiedRate' as keyof FormulaRow,
  //   key: 'modifiedRate',
  //   width: 120,
  //   align: 'right' as const,
  //   render: (v: number | undefined) =>
  //     v !== undefined && Number.isFinite(v) ? (
  //       <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#7c3aed' }}>
  //         £{v.toFixed(2)}
  //       </span>
  //     ) : (
  //       <span style={{ color: '#d1d5db' }}>—</span>
  //     ),
  // },
  {
    title: 'Note',
    dataIndex: 'note' as keyof FormulaRow,
    key: 'note',
    width: 160,
    render: (v: string) => <span style={{ color: '#6b7280', fontSize: 12 }}>{v || '—'}</span>,
  },
];

// ─── Public: modal body content ───────────────────────────────────────────────

export function getFormulaModalContent(
  categoryName: string,
  fieldNames: string[],
  groundworks?: any,
  rateByCode?: Map<string, number>,
  boqRateByField?: Map<string, number>,
): React.ReactNode {
  let def: FormulaDefinition;
  if (categoryName === 'Site Strip - Topsoil Disposal') {
    def = buildTopsoilDisposalDefinition(groundworks);
  } else if (categoryName === 'Cut/Fill - Disposal Of Surplus') {
    def = buildSubsoilDefinition(groundworks, 'Surplus');
  } else if (categoryName === 'Cut/Fill - Import of Deficit') {
    def = buildSubsoilDefinition(groundworks, 'Deficit');
  } else if (categoryName === 'Screen Walls - upto DPC') {
    def = buildScreenWallsUptoDpcDefinition(groundworks);
  } else {
    def = FORMULA_DEFINITIONS[categoryName] ?? buildGenericDefinition(fieldNames);
  }

  // Plot Drives dev-mode code picks (colKey → code) replace the static code so the
  // inspector shows, and looks up the rate of, the code actually used for pricing.
  const plotDrivesCodePicks = new Map<string, string>();
  Object.entries((groundworks?.plotDrivesCodeOverrides as Record<string, string> | undefined) ?? {}).forEach(([colKey, pick]) => {
    const field = PLOT_DRIVES_COLUMN_MAPPING[colKey];
    if (field && pick) plotDrivesCodePicks.set(field, pick);
  });

  const enrichRow = (row: FormulaRow, i: number): FormulaRow => {
    const picked = plotDrivesCodePicks.get(row.fieldName);
    const r = picked ? { ...row, resourceCode: picked.toUpperCase() } : row;
    const code = r.resourceCode?.trim().toUpperCase();
    const rate = code && rateByCode ? rateByCode.get(code) : undefined;
    const modifiedRate = boqRateByField ? boqRateByField.get(r.fieldName) : undefined;
    return { ...r, key: `${r.fieldName}-${i}`, rate, modifiedRate };
  };

  return (
    <div style={{ fontSize: 13 }}>
      <div style={{ marginBottom: 14, color: '#6b7280', fontStyle: 'italic', whiteSpace: 'pre-line' }}>{def.summary}</div>
      {def.parts.map((part, pi) => (
        <div key={pi} style={{ marginBottom: pi < def.parts.length - 1 ? 20 : 0 }}>
          {part.label && (
            <div style={{ fontWeight: 600, color: '#374151', marginBottom: 8 }}>{part.label}</div>
          )}
          <Table<FormulaRow>
            columns={MODAL_TABLE_COLUMNS}
            dataSource={part.rows.map(enrichRow)}
            pagination={false}
            size="small"
            bordered
          />
        </div>
      ))}
    </div>
  );
}
